import dayjs from "dayjs";
import Decimal from 'decimal.js';
import i18n from './i18n';
import {
  CBomDataWithMonthly,
  Currency,
  DefectCostResult,
  DefectCostResultByMonth,
  DevelopmentMonthCostTable,
  EquipmentExtType,
  EquipmentType,
  MoldMonthTable,
  MonthData,
  MonthlyExpenseData,
  MonthValue,
  NonWorkingHoursType,
  PartNo,
  ProcessBomRowForCalculation,
  ProjectWithRdsData,
  QuantityAndPriceMonthTable,
  QuantityAndPriceTable,
  UniqueProcessType,
  WorkingDaysType,
} from '../contexts/types';
import {
  convertCurrency,
} from '../utils/CurrencyConverter';
import type { ConvertFn } from './reviewCalculation';
import {
  getVisibleMonths,
  hourMinutesStringToMinutes,
} from '../utils/utils';

Decimal.set({ precision: 50 });

/**
 * 総会計年度数の取得（PartNoのみ参照）
 * @param capexPartNo M_CEAS_PART_NO PartNoデータ
 * @returns 総会計年度数 （例: 5）
 */
export const getTotalFiscalYearsByPartNo = (
  capexPartNo: PartNo[]
): number => {
  const functionName = "getTotalFiscalYearsByPartNo";
  try {

    // PartNoデータから最大年数を取得
    const maxFiscalYearsFromPartNo =
      capexPartNo.reduce((currentMaxYear, part) => {
        // 数量単価が存在する場合のみ処理
        if (!part.quantityAndPrice) return currentMaxYear;
        // JSON文字列をオブジェクトに変換
        const parsedQuantityAndPrice: QuantityAndPriceTable = JSON.parse(part.quantityAndPrice);
        // 現在の最大値を取得
        const partMaxYear = Math.max(...parsedQuantityAndPrice.years);
        // 最大値を更新
        return Math.max(currentMaxYear, partMaxYear);
      }, 0);

    // 総会計年度数を返却
    return maxFiscalYearsFromPartNo;

  } catch (error) {
    console.error(`${functionName} ERROR: `, error);
    return 0;
  }
}

/**
 * 総会計年度数の取得（PartNo, PJ別金型費回収, PJ別開発費回収を参照）
 * @param capexPartNo M_CEAS_PART_NO PartNoデータ
 * @param moldSalesByProject T_CEAS_PROJECT PJ別金型費回収データ
 * @param developmentSalesByProject T_CEAS_PROJECT PJ別開発費回収データ
 * @param equipmentSalesByProject T_CEAS_PROJECT PJ別設備費回収データ
 * @returns 総会計年度数 （例: 5）
 */
export const getTotalFiscalYearsByProject = (
  capexPartNo: PartNo[],
  moldSalesByProject: string | null | undefined,
  developmentSalesByProject: string | null | undefined,
  equipmentSalesByProject: string | null | undefined
): number => {
  const functionName = "getTotalFiscalYears";
  try {

    // JSONパースしてPJ別金型費回収データを取得（currencyIdを含む）
    const parsedMoldSales: any[] = moldSalesByProject
      ? JSON.parse(moldSalesByProject)
      : [];

    // JSONパースしてPJ別開発費回収データを取得（currencyIdを含む）
    const parsedDevelopmentSales: any[] = developmentSalesByProject
      ? JSON.parse(developmentSalesByProject)
      : [];

    // JSONパースしてPJ別設備費回収データを取得（currencyIdを含む）
    const parsedEquipmentSales: any[] = equipmentSalesByProject
      ? JSON.parse(equipmentSalesByProject)
      : [];

    // PartNoデータから最大年数を取得
    const maxFiscalYearsFromPartNo =
      capexPartNo.reduce((currentMaxYear, part) => {
        // 数量単価が存在する場合のみ処理
        if (!part.quantityAndPrice) return currentMaxYear;
        // JSON文字列をオブジェクトに変換
        const parsedQuantityAndPrice: QuantityAndPriceTable = JSON.parse(part.quantityAndPrice);
        // 現在の最大値を取得
        const partMaxYear = Math.max(...parsedQuantityAndPrice.years);
        // 最大値を更新
        return Math.max(currentMaxYear, partMaxYear);
      }, 0);

    // PJ別金型費回収データから最大年数を取得
    const maxFiscalYearsFromMoldSales =
      parsedMoldSales.reduce((currentMaxYear, moldData) => {
        // 現在の最大値を取得
        const moldMaxYear = Math.max(...moldData.years);
        // 最大値を更新
        return Math.max(currentMaxYear, moldMaxYear);
      }, 0);

    // PJ別開発費回収データから最大年数を取得
    const maxFiscalYearsFromDevelopmentSales =
      parsedDevelopmentSales.reduce((currentMaxYear, developmentData) => {
        // 現在の最大値を取得
        const developmentMaxYear = Math.max(...developmentData.years);
        // 最大値を更新
        return Math.max(currentMaxYear, developmentMaxYear);
      }, 0);

    // PJ別設備費回収データから最大年数を取得
    const maxFiscalYearsFromEquipmentSales =
      parsedEquipmentSales.reduce((currentMaxYear, equipmentData) => {
        // 現在の最大値を取得
        const equipmentMaxYear = Math.max(...equipmentData.years);
        // 最大値を更新
        return Math.max(currentMaxYear, equipmentMaxYear);
      }, 0);

    // 全ての最大値を比較して取得
    const totalFiscalYears = Math.max(
      maxFiscalYearsFromPartNo,
      maxFiscalYearsFromMoldSales,
      maxFiscalYearsFromDevelopmentSales,
      maxFiscalYearsFromEquipmentSales,
    );

    // 5. 総会計年度数を返却
    return totalFiscalYears;

  } catch (error) {
    console.error(`${functionName} ERROR: `, error);
    return 0;
  }
}

/**
 * 量産開始日とEOL予定日から総会計年度数を算出する（簡易モード用）。
 * 4月～翌3月を1年度として、量産開始日の年度〜EOL予定日の年度までの年数を返す。
 *
 * @param projectStartDate 量産開始日（YYYY-MM-DD）
 * @param projectEndDate EOL予定日（YYYY-MM-DD）
 * @returns 総会計年度数（最小1、最大10）
 */
export const getFiscalYearsByProjectDates = (
  projectStartDate: string | null | undefined,
  projectEndDate: string | null | undefined,
): number => {
  if (!projectStartDate || !projectEndDate) return 0;

  const startDate = new Date(projectStartDate);
  const startYear = startDate.getFullYear();
  const startMonth = startDate.getMonth() + 1;
  const startFiscalYear = startMonth >= 4 ? startYear : startYear - 1;

  const endYear = parseInt(projectEndDate.split("-")[0], 10);
  const endMonth = parseInt(projectEndDate.split("-")[1], 10);
  const endFiscalYear = endMonth >= 4 ? endYear : endYear - 1;

  const maxYear = endFiscalYear - startFiscalYear + 1;
  if (maxYear <= 0) return 1;
  return Math.min(maxYear, 10);
}

/**
 * 年度文字列配列の取得
 * @param projectStartDate プロジェクト開始日（投資設備検収日）
 * @param totalFiscalYearsByProject 総会計年度数
 * @param useLabel true：ラベル付き(省略時)、false：ラベルなし
 * @returns 年度配列 （例: ["2025年3月期", "2026年3月期"...]）
 */
export const getFiscalYearsString = (
  projectStartDate: string,
  totalFiscalYearsByProject: number,
  useLabel?: boolean
): string[] => {
  const functionName = "getFiscalYearsString";
  try {

    // プロジェクト開始日をオブジェクトに変換
    const startDateObj = dayjs(projectStartDate);

    // 開始年度を決定（3月期基準）
    // - 1〜3月の場合は当年を会計年度（○○年3月期）として扱う
    // - 4〜12月の場合は翌年を会計年度（○○年3月期）として扱う
    // 例: 2024-04-01 → 2025年3月期、2025-01-01 → 2025年3月期
    const startFiscalYear = (startDateObj.month() + 1) <= 3 ? startDateObj.year() : startDateObj.year() + 1;

    // 年度文字列配列を作成
    const fiscalYears = Array.from({ length: totalFiscalYearsByProject }, (_, i) => {
      const year = startFiscalYear + i;
      return useLabel === false ? `${year}` : i18n.t('common.dateFormat.fiscalYear', { year });
    });

    // 年度文字列配列を返却
    return fiscalYears;

  } catch (error) {
    console.error(`${functionName} ERROR: `, error);
    return [];
  }
}

/**
 * 2つの会計年度文字列が同じ年度を指しているかを判定する。
 * 会計年度文字列は表示言語によってフォーマットが異なる（例：日本語「2025年3月期」／英語「FY2025」）ため、
 * 文字列の完全一致ではなく、含まれる年数値を抽出して比較する。
 * これにより、保存済みデータと表示中の言語が異なる場合でも正しく同一年度と判定できる。
 * @param a 会計年度文字列
 * @param b 会計年度文字列
 * @returns true：同じ年度を指している、false：異なる（またはどちらかが不正な値）
 */
export const isSameFiscalYear = (a: string | undefined, b: string | undefined): boolean => {
  if (!a || !b) return false;
  const yearA = a.match(/\d+/)?.[0];
  const yearB = b.match(/\d+/)?.[0];
  if (!yearA || !yearB) return false;
  return yearA === yearB;
}


/**
 * 年月文字列配列の取得
 * @param projectStartDate プロジェクト開始日（投資設備検収日）
 * @param totalFiscalYearsByProject 総会計年度数
 * @param useLabel true：ラベル付き(省略時)、false：ラベルなし
 * @returns 年月配列 （例: [{"year":"2025", "month":"3"}, {"year":"2025", "month":"4"}...]）
 */
export const getFiscalMonthsString = (
  projectStartDate: string,
  totalFiscalYears: number,
  useLabel: boolean = true
) => {
  const start = dayjs(projectStartDate);

  // 開始年度（3月期基準）
  const startFiscalYear =
    start.month() + 1 <= 3 ? start.year() : start.year() + 1;

  // 終了年度の3月
  const endMarch = dayjs(
    `${startFiscalYear + (totalFiscalYears - 1)}-03-01`
  );

  const result = [];
  let cursor = start;

  while (cursor.isBefore(endMarch) || cursor.isSame(endMarch, "month")) {
    if (useLabel) {
      result.push(i18n.t('common.dateFormat.yearMonth', { year: cursor.year(), month: cursor.month() + 1 }));
    } else {
      result.push({
        year: cursor.year(),
        month: cursor.month() + 1,
      });
    }
    cursor = cursor.add(1, "month");
  }

  return result;
};

/**
 * 年月リストを生成
 * @param projectStartDate プロジェクト開始日（投資設備検収日）
 * @param totalFiscalYearsByProject 総会計年度数
 * @param isAdjustedYear TRUE: yearを年数でカウント, FALSE: yearを年度でカウント
 * @returns 年月リスト
 * （例: [{ year: 1, month: 1 },{ year: 1, month: 2 }...]）
 * （例: [{ year: 2023, month: 1 },{ year: 2023, month: 2 }...]）
 */
export const generateYearMonthList = (
  projectStartDate: string | null | undefined,
  totalFiscalYearsByProject: number,
  isAdjustedYear: boolean = false
): { year: number; month: number }[] => {
  const functionName = "generateYearMonthList";
  try {

    // プロジェクト開始日が無効な場合は空配列を返却
    if (!projectStartDate) {
      return [];
    }

    // プロジェクト開始日をオブジェクトに変換
    const startDateObj = dayjs(projectStartDate);
    const startMonth = startDateObj.month() + 1; // 開始月 (dayjsのmonthは0-11のため+1)

    // 初年度の月数を計算
    const firstFiscalMonths = startMonth < 4 ? 4 - startMonth : 12 - (startMonth - 4);

    // 年度ごとの月数リストを生成
    const monthsPerYear = [
      firstFiscalMonths, // 初年度の月数
      ...Array(totalFiscalYearsByProject - 1).fill(12), // 各年度12ヶ月
    ];

    // 総月数を計算
    const totalMonths = monthsPerYear.reduce((sum, m) => sum + m, 0);

    // 年月リストを生成
    const yearMonthList: { year: number; month: number }[] = [];
    let currentDate = startDateObj; // 開始日を基点とする初期値
    let adjustedYear = 1; // 調整された年数（1から始まる）

    for (let monthIndex = 0; monthIndex < totalMonths; monthIndex++) {
      // 年の選択（調整された年 or 現在の年）
      const year = isAdjustedYear ? adjustedYear : currentDate.year();

      // 年月をリストに追加
      yearMonthList.push({ year, month: currentDate.month() + 1 });

      // 次の月へ進む
      currentDate = currentDate.add(1, "month");

      // 年度切り替え処理
      if (currentDate.month() + 1 === 4 && isAdjustedYear) {
        adjustedYear++; // 4月 → 次年度
      }
    }

    // 年月リストを返却
    return yearMonthList;

  } catch (error) {
    console.error(`${functionName} ERROR:`, error);
    return [];
  }
};

/**
 * Decimal型配列を年月オブジェクトに変換
 * @param yearMonthList 年月リスト
 * @param arrays Decimal配列
 * @returns 年月オブジェクト
 * （例: [{ year: 1, month: 1, value: 1000 },{ year: 1, month: 2, value: 1000 }...]）
 * （例: [{ year: 2023, month: 1, value: 1000 },{ year: 2023, month: 2, value: 1000 }...]）
 */
export const convertToYearMonthObject = (
  yearMonthList: { year: number; month: number }[],
  ...arrays: Decimal[][]
): { year: number; month: number; value: Decimal }[] => {
  const functionName = "convertToYearMonthObject";
  try {

    // 年月リストの長さを基準として優先
    const maxLength = yearMonthList.length;

    // 配列要素の数が年月リストよりも短い場合は補完（長い場合は切り捨て）
    const normalizedArrays = arrays.map((array) =>
      Array.from({ length: maxLength }, (_, index) => array[index] || new Decimal(0))
    );

    // 年月リストに基づきオブジェクトを生成
    const result = yearMonthList.map((item, index) => {
      const total = normalizedArrays.reduce(
        (sum, array) => sum.plus(array[index] || new Decimal(0)), // 配列要素が無い場合は Decimal(0) を加算
        new Decimal(0) // 初期値
      );

      // 年月リストから年と月を取得して結果を整形
      return { year: item.year, month: item.month, value: total };
    });

    // 年月オブジェクトを返却
    return result;

  } catch (error) {
    console.error(`${functionName} ERROR: `, error);
    return [];
  }
};

/**
 * PartNo別数量の取得 (月別)
 *
 * @param {PartNo[]} capexPartNo M_CEAS_PART_NO データ
 * @param {string} projectStartDate プロジェクト開始日
 * @returns {{ partNo: string, year: number, quantities: Decimal[] }[]} 月別 PartNo 数量データ
 */
export const getQuantitiesByPartNoMonth = (
  capexPartNo: PartNo[],
  projectStartDate: string
): { partNo: string, year: number, quantities: Decimal[] }[] => {
  const functionName = "getQuantitiesByPartNoMonth";
  try {

    // プロジェクト開始月を取得
    const startMonth = projectStartDate
      ? parseInt(projectStartDate.split("-")[1], 10)
      : 0;

    return capexPartNo.flatMap((part) => {
      const partNo = part.partNo || "";
      if (!part.partNo || !part.quantityAndPrice) return [];

      // JSON文字列をオブジェクトに変換
      const parsedData: QuantityAndPriceMonthTable = JSON.parse(part.quantityAndPrice);

      // 年度別の月次数量を取得
      return parsedData.years.map((year) => {
        const yearData = parsedData.data[year];
        const monthlyQuantities: Decimal[] = [];

        if (!yearData || !yearData.months) {
          return { partNo: partNo, year, quantities: [] };
        }

        // 初年度はプロジェクト開始月以降のデータのみ利用
        const visibleMonthKeys = getVisibleMonths(year, yearData.months, startMonth || 0);

        // 各月の数量を月次配列に追加
        for (const [monthIndex, rawMonthData] of Object.entries(yearData.months)) {
          const monthData = rawMonthData as MonthData;
          const monthKey = Number(monthIndex);
          // 初年度表示対象外月の場合スキップ
          if (year === 1 && !visibleMonthKeys.includes(monthKey)) continue;

          monthlyQuantities.push(new Decimal(monthData.quantity || 0));
        }

        return {
          partNo: partNo,                // PartNo
          year,                          // 年度
          quantities: monthlyQuantities, // 月次数量配列
        };
      });
    });
  } catch (error) {
    console.error(`${functionName} ERROR: `, error);
    return [];
  }
};

/**
 * PartNo別数量の取得 (年度別)
 *
 * @param {{ partNo: string, year: number, quantities: Decimal[] }[]} quantitiesByPartNoMonth 月別 PartNo 数量データ
 * @returns {{ partNo: string, quantities: Decimal[] }[]} 年度別 PartNo 数量データ
 */
export const getQuantitiesByPartNoYear = (
  quantitiesByPartNoMonth: { partNo: string, year: number, quantities: Decimal[] }[]
): { partNo: string, quantities: Decimal[] }[] => {
  const functionName = "getQuantitiesByPartNoYear";
  try {
    // PartNo別の年度データを作成
    const aggregatedData: { [partNo: string]: Decimal[] } = {};

    quantitiesByPartNoMonth.forEach(({ partNo, year, quantities }) => {
      const yearIndex = year - 1; // 年度インデックス

      if (!aggregatedData[partNo]) {
        aggregatedData[partNo] = [];
      }

      // 年度別数量を合算
      aggregatedData[partNo][yearIndex] = (aggregatedData[partNo][yearIndex] || new Decimal(0))
        .plus(quantities.reduce((sum, quantity) => sum.plus(quantity), new Decimal(0)));
    });

    // データをフォーマットして返却
    return Object.entries(aggregatedData).map(([partNo, quantities]) => ({
      partNo,
      quantities,
    }));
  } catch (error) {
    console.error(`${functionName} ERROR: `, error);
    return [];
  }
};

/**
 * PartNo別数量 を String形式に変換
 *
 * @param {{ partNo: string, year: number, quantities: Decimal[] }[]} quantitiesByPartNoMonth 月別 PartNo 数量データ
 * @param {number} totalMonths 月数
 * @returns {{ [partNo: string]: string[] }} PartNo をキー、月別数量を値とするデータ構造
 */
export const convertQuantitiesByPartNoMonth = (
  quantitiesByPartNoMonth: { partNo: string, year: number, quantities: Decimal[] }[],
  totalMonths: number
): { [partNo: string]: string[] } => {
  const functionName = "convertQuantitiesByPartNoMonth";
  try {
    // データ変換結果を格納するオブジェクト
    const result: { [partNo: string]: string[] } = {};

    quantitiesByPartNoMonth.forEach(({ partNo, quantities }) => {
      if (!result[partNo]) {
        // PartNoが未定義の場合は初期化
        result[partNo] = [];
      }

      // 月次数量を文字列として登録（string[]）
      result[partNo].push(...quantities.map(quantity => quantity.toFixed())); // Decimalを文字列に変換
    });

    // 要素数を月数に揃える（不足分は"0"埋め）
    Object.keys(result).forEach(partNo => {
      const currentQuantities = result[partNo];
      const diff = totalMonths - currentQuantities.length; // 足りない月数を計算
      if (diff > 0) {
        result[partNo].push(...Array(diff).fill(new Decimal(0).toFixed())); // "0"埋め
      }
    });

    return result;
  } catch (error) {
    console.error(`${functionName} ERROR: `, error);
    return {};
  }
};

/**
 * PartNo別単価の取得(月別)
 *
 * @param {PartNo[]} capexPartNo M_CEAS_PART_NO データ
 * @param {string} sourceCurrencyName 変換元通貨名
 * @param {string} targetCurrencyName 変換先通貨名
 * @param {Currency[]} currencyData 通貨データ配列
 * @param {string} projectStartDate プロジェクト開始日
 * @returns {{ partNo: string, year: number, prices: Decimal[] }[]} 月別 PartNo 単価データ
 */
export const getPricesByPartNoMonth = (
  capexPartNo: PartNo[],
  targetCurrencyName?: string,
  currencyData?: Currency[],
  projectStartDate?: string,
  convertFn?: ConvertFn
): { partNo: string, year: number, prices: Decimal[] }[] => {
  const functionName = "getPricesByPartNoMonth";
  try {
    const convert = convertFn
      || ((amount: number | Decimal, from: string, to: string) => convertCurrency(amount, from, to, currencyData!));

    // プロジェクト開始月を取得
    const startMonth = projectStartDate
      ? parseInt(projectStartDate.split("-")[1], 10)
      : 0;
    const startYear = projectStartDate
      ? parseInt(projectStartDate.split("-")[0], 10)
      : 0;

    return capexPartNo.flatMap(part => {
      const partNo = part.partNo || "";
      if (!part.quantityAndPrice) return [];

      // JSON文字列をオブジェクトに変換
      const parsedData: QuantityAndPriceMonthTable = JSON.parse(part.quantityAndPrice);

      // PartNo単位で販売通貨を取得
      // currencyAndRatioはJSON文字列なのでパースする
      let currencyAndRatio: { id: string; ratio: number }[] = [];
      if (parsedData.currencyAndRatio) {
        try {
          currencyAndRatio = typeof parsedData.currencyAndRatio === 'string'
            ? JSON.parse(parsedData.currencyAndRatio)
            : parsedData.currencyAndRatio;
        } catch (error) {
          console.error(`[${functionName}] Failed to parse currencyAndRatio for PartNo ${partNo}:`, error);
        }
      }

      let sourceCurrencyName = "";

      if (currencyAndRatio.length > 0) {
        // 最大比率を持つ通貨を販売通貨として使用
        const maxRatio = Math.max(...currencyAndRatio.map(c => c.ratio));
        const firstMaxIndex = currencyAndRatio.findIndex(c => c.ratio === maxRatio);
        const salesCurrencyId = currencyAndRatio[firstMaxIndex]?.id;

        if (salesCurrencyId && currencyData) {
          const currency = currencyData.find(c => c.currencyId === salesCurrencyId);
          sourceCurrencyName = currency?.currencyName || "";
        }
      }

      // 年度別の月次単価を取得
      return parsedData.years.map((year) => {
        const yearData = parsedData.data[year];
        const monthlyPrices: Decimal[] = [];

        if (!yearData || !yearData.months) {
          return { partNo, year, prices: [] };
        }

        const visibleMonthKeys = getVisibleMonths(year, yearData.months, startMonth);

        // 月次の単価を格納
        // 配列位置カウンタ（getTotalSalesByMonth が index 0 を startMonth として解釈するため、
        // yearMonth は配列位置ベースで算出する必要がある）
        let arrayIndex = 0;
        for (const [monthIndex, rawMonthData] of Object.entries(yearData.months)) {
          const monthData = rawMonthData as MonthData;
          const monthKey = Number(monthIndex);

          // 初年度表示対象外月の場合スキップ
          if (year === 1 && !visibleMonthKeys.includes(monthKey)) continue;

          // 為替変換
          const price = monthData.price || 0;
          // カレンダー年月を算出（配列位置ベース: index 0 = startMonth）
          // getTotalSalesByMonth の satrtMonth 計算と一致させる
          const fiscalMonth = startMonth + arrayIndex;
          const actualMonth = fiscalMonth > 12 ? fiscalMonth - 12 : fiscalMonth;
          const calYear = startMonth >= 4
            ? startYear + (year - 1) + (actualMonth >= 1 && actualMonth <= 3 ? 1 : 0)
            : startYear + (year - 2) + (actualMonth >= 4 ? 0 : 1);
          const yearMonth = `${calYear}-${String(actualMonth).padStart(2, "0")}`;
          const convertedPrice = sourceCurrencyName && targetCurrencyName && (convertFn || currencyData)
            ? convert(price, sourceCurrencyName, targetCurrencyName, yearMonth)
            : price;


          monthlyPrices.push(new Decimal(convertedPrice));
          arrayIndex++;
        }

        return {
          partNo,           // PartNo
          year,             // 年次情報
          prices: monthlyPrices, // 月次単価配列
        };
      });
    });
  } catch (error) {
    console.error(`${functionName} ERROR: `, error);
    return [];
  }
};

/**
 * PartNo別単価(加重平均)の取得(年度別)
 *
 * @param {{ partNo: string, year: number, prices: Decimal[] }[]} pricesByPartNoMonth 月別単価データ
 * @param {{ partNo: string, year: number, quantities: Decimal[] }[]} quantitiesByPartNoMonth 月別数量データ
 * @returns {{ partNo: string, prices: Decimal[] }[]} 年度別 PartNo 単価データ（加重平均）
 */
export const getPricesByPartNoYear = (
  pricesByPartNoMonth: { partNo: string, year: number, prices: Decimal[] }[],
  quantitiesByPartNoMonth: { partNo: string, year: number, quantities: Decimal[] }[]
): { partNo: string, prices: Decimal[] }[] => {
  const functionName = "getPricesByPartNoYear";
  try {
    // PartNo別の年度データを初期化
    const aggregatedData: { [partNo: string]: { [year: number]: { totalPrice: Decimal, totalQuantity: Decimal } } } = {};

    // 月別の単価と数量をループして集計
    pricesByPartNoMonth.forEach(({ partNo, year, prices }) => {
      const quantities = quantitiesByPartNoMonth.find(
        (qData) => qData.partNo === partNo && qData.year === year
      )?.quantities;

      if (!quantities) {
        console.warn(`${functionName}: No quantities found for partNo: ${partNo}, year: ${year}`);
        return;
      }

      if (!aggregatedData[partNo]) {
        aggregatedData[partNo] = {};
      }

      if (!aggregatedData[partNo][year]) {
        aggregatedData[partNo][year] = { totalPrice: new Decimal(0), totalQuantity: new Decimal(0) };
      }

      // 月ごとの単価・数量を加算
      prices.forEach((price, index) => {
        const quantity = quantities[index] || new Decimal(0);
        aggregatedData[partNo][year].totalPrice = aggregatedData[partNo][year].totalPrice.plus(price.times(quantity));
        aggregatedData[partNo][year].totalQuantity = aggregatedData[partNo][year].totalQuantity.plus(quantity);
      });
    });

    // 年度別データから加重平均単価を計算
    return Object.entries(aggregatedData).map(([partNo, yearlyData]) => ({
      partNo,
      prices: Object.keys(yearlyData).map((year) => {
        const { totalPrice, totalQuantity } = yearlyData[+year];
        return totalQuantity.gt(0) ? totalPrice.div(totalQuantity) : new Decimal(0);
      }),
    }));
  } catch (error) {
    console.error(`${functionName} ERROR: `, error);
    return [];
  }
};

/**
 * PartNo別売上金額の算出(月別 & 年度別)
 *
 * @param {{ partNo: string, year: number, quantities: Decimal[] }[]} quantitiesByPartNoMonth PartNo別数量(月別)データ
 * @param {{ partNo: string, year: number, prices: Decimal[] }[]} pricesByPartNoMonth PartNo別単価(月別)データ
 * @param {boolean} decimalCurrency 小数有無（小数点第二位を制御するかどうか）
 * @returns {
 *    totalSalesByPartNoMonth: { partNo: string, year: number, totalSales: Decimal[] }[],
 *    totalSalesByPartNoYear: { partNo: string, totalSales: Decimal[] }[]
 * } PartNo別売上金額(月別 & 年度別)
 */
export const calcTotalSalesByPartNo = (
  quantitiesByPartNoMonth: { partNo: string, year: number, quantities: Decimal[] }[],
  pricesByPartNoMonth: { partNo: string, year: number, prices: Decimal[] }[],
  decimalCurrency: boolean
): {
  totalSalesByPartNoMonth: { partNo: string, year: number, totalSales: Decimal[] }[],
  totalSalesByPartNoYear: { partNo: string, totalSales: Decimal[] }[],
} => {
  const functionName = "calcTotalSalesByPartNo";
  try {
    // 月別売上金額を計算
    const totalSalesByPartNoMonth = calcTotalSalesByPartNoMonth(quantitiesByPartNoMonth, pricesByPartNoMonth);

    // 年度別売上金額を集計
    const totalSalesByPartNoYear = calcTotalSalesByPartNoYear(totalSalesByPartNoMonth, decimalCurrency);

    return {
      totalSalesByPartNoMonth,
      totalSalesByPartNoYear,
    };
  } catch (error) {
    console.error(`${functionName} ERROR: `, error);
    return {
      totalSalesByPartNoMonth: [],
      totalSalesByPartNoYear: [],
    };
  }
};

/**
 * PartNo別売上金額の算出(月別)
 *
 * @param {{ partNo: string, year: number, quantities: Decimal[] }[]} quantitiesByPartNoMonth PartNo別数量(月別)データ
 * @param {{ partNo: string, year: number, prices: Decimal[] }[]} pricesByPartNoMonth PartNo別単価(月別)データ
 * @returns {{ partNo: string, year: number, totalSales: Decimal[] }[]} PartNo別売上金額(月別)
 */
export const calcTotalSalesByPartNoMonth = (
  quantitiesByPartNoMonth: { partNo: string, year: number, quantities: Decimal[] }[],
  pricesByPartNoMonth: { partNo: string, year: number, prices: Decimal[] }[]
): { partNo: string, year: number, totalSales: Decimal[] }[] => {
  const functionName = "calcTotalSalesByPartNoMonth";
  try {
    return quantitiesByPartNoMonth.map(quantityData => {
      const partNo = quantityData.partNo;
      const year = quantityData.year;

      // 対応する単価データを取得
      const priceData = pricesByPartNoMonth.find(item => item.partNo === partNo && item.year === year);

      // 数量配列と単価配列を初期化
      const quantities = quantityData.quantities;
      const prices = priceData ? priceData.prices : [];

      // 月別の売上金額を計算
      const totalSales = quantities.map((quantity, index) => {
        const price = prices[index] || new Decimal(0);
        return quantity.times(price);
      });

      return {
        partNo,       // PartNo
        year,         // 年度情報
        totalSales,   // 月別売上金額配列
      };
    });
  } catch (error) {
    console.error(`${functionName} ERROR: `, error);
    return [];
  }
};

/**
 * PartNo別売上金額の算出(年度別)
 *
 * @param {{ partNo: string, year: number, totalSales: Decimal[] }[]} totalSalesByPartNoMonth PartNo別売上金額(月別)
 * @param {boolean} decimalCurrency 未使用（No425対応: 丸めを撤廃したため。呼び出し元との互換のため引数は残す）
 * @returns {{ partNo: string, totalSales: Decimal[] }[]} PartNo別売上金額(年度別、丸めなし)
 */
export const calcTotalSalesByPartNoYear = (
  totalSalesByPartNoMonth: { partNo: string, year: number, totalSales: Decimal[] }[],
  decimalCurrency: boolean
): { partNo: string, totalSales: Decimal[] }[] => {
  const functionName = "calcTotalSalesByPartNoYear";
  try {
    let result: { partNo: string, totalSales: Decimal[] }[] = [];
    result = totalSalesByPartNoMonth.reduce<{ partNo: string, totalSales: Decimal[] }[]>(
      (acc, salesData) => {
        const partNo = salesData.partNo;
        const year = salesData.year;

        // 存在する PartNo データを取得、なければ新規作成
        let partData = acc.find(item => item.partNo === partNo);
        if (!partData) {
          partData = { partNo, totalSales: [] };
          acc.push(partData);
        }

        // 該当年度の売上金額を合算
        const yearIndex = year - 1; // 年度のインデックスを計算
        partData.totalSales[yearIndex] = (partData.totalSales[yearIndex] || new Decimal(0))
          .plus(salesData.totalSales.reduce((sum, value) => sum.plus(value), new Decimal(0)));
        return acc;
      },
      []
    );
    return result;
  } catch (error) {
    console.error(`${functionName} ERROR: `, error);
    return [];
  }
};

/**
 * PartNoごとに内製比率の加重平均を計算
 *
 * @param {PartNo[]} partNoData PartNo全体のデータ
 * @returns {{ partNo: string, inhouseAvgRatio: Decimal }[]} PartNoごとの内製比率の加重平均
 */
export const calculateInhouseRatioByPartNo = (partNoData: PartNo[]): { partNo: string, inhouseAvgRatio: Decimal }[] => {
  const functionName = "calculateInhouseRatioByPartNo";
  try {
    return partNoData.map(part => {
      if (!part.partNo || !part.materialCostWithInhouseProfit) {
        return { partNo: part.partNo || "", inhouseAvgRatio: new Decimal(0) }; // データが不完全な場合は 0 を返す
      }

      // JSONデータを加工
      let parsedData: any[] = [];
      try {
        parsedData = JSON.parse(part.materialCostWithInhouseProfit);
      } catch (error) {
        console.warn(`${functionName} JSON parse failed for partNo: ${part.partNo}`, error);
        return { partNo: part.partNo, inhouseAvgRatio: new Decimal(0) }; // JSON が不正な場合は 0 を返す
      }

      // 内製比率の加重平均値を計算
      const { weightedNumerator, totalAdjustedAmount } = parsedData.reduce(
        (acc, material) => {
          const inhouseRatio = new Decimal(material.inhouseRatio || 0).dividedBy(100); // 内製比率を小数化
          const adjustedAmount = new Decimal(material.InternalAdjustedAmount || 0);   // 内製利益を考慮した金額

          // 分子: 内製比率 × AdjustedAmount
          acc.weightedNumerator = acc.weightedNumerator.plus(inhouseRatio.times(adjustedAmount));
          // 分母: AdjustedAmount の合計
          acc.totalAdjustedAmount = acc.totalAdjustedAmount.plus(adjustedAmount);

          return acc;
        },
        {
          weightedNumerator: new Decimal(0),     // 分子の初期値
          totalAdjustedAmount: new Decimal(0),   // 分母の初期値
        }
      );

      // 加重平均値を計算
      const weightedAverage = totalAdjustedAmount.equals(new Decimal(0))
        ? new Decimal(0) // AdjustedAmountがゼロの場合結果はゼロ
        : weightedNumerator.dividedBy(totalAdjustedAmount).times(100); // 百分率に変換

      return {
        partNo: part.partNo,
        inhouseAvgRatio: weightedAverage.toDecimalPlaces(2, Decimal.ROUND_HALF_UP), // 小数点2位まで丸め
      };
    });
  } catch (error) {
    console.error(`${functionName} ERROR:`, error);
    return [];
  }
};

/**
 * PartNo別スループット額(年度別)
 * 金型、開発費を含むスループットを算出する
 * 
 * @param {{ partNo: string, totalSales: Decimal[] }[]} totalSalesMergedByYear 合算された売上年度別合計 (製品売上高、金型、開発費)
 * @param {{ partNo: string, totalMaterialCosts: Decimal[] }[]} totalMaterialCostsByYear 年度別材料費合計
 * @returns {{ partNo: string, throughputs: Decimal[] }[]} PartNoごとの年度別スループット額
 */
export const mergeSalesAndSubtractCosts = (
  totalSalesMergedByYear: { partNo: string, totalSales: Decimal[] }[],
  totalMaterialCostsByYear: { partNo: string, totalMaterialCosts: Decimal[] }[]
): { partNo: string, throughputs: Decimal[] }[] => {
  const functionName = "mergeSalesAndSubtractCosts";

  try {
    console.log(`${functionName} START`);

    const throughputResults: Record<string, Decimal[]> = {};

    // totalSalesMergedByYear の情報を基準に計算開始
    totalSalesMergedByYear.forEach(({ partNo, totalSales }) => {
      // 該当する PartNo の材料費データを取得
      const materialData = totalMaterialCostsByYear.find(item => item.partNo === partNo);
      const materialCosts = materialData ? materialData.totalMaterialCosts : [];

      // PartNo の年度別スループットを計算
      const throughputs = totalSales.map((sales, yearIndex) => {
        const materialCost = materialCosts[yearIndex] || new Decimal(0); // 材料費がない場合は 0
        return sales.minus(materialCost); // 売上高 ー 材料費
      });

      throughputResults[partNo] = throughputs;
    });

    // スループット結果を配列形式に変換
    const result = Object.entries(throughputResults).map(([partNo, throughputs]) => ({
      partNo,
      throughputs,
    }));

    return result;
  } catch (error) {
    console.error(`${functionName} ERROR: `, error);
    return [];
  } finally {
    console.log(`${functionName} END`);
  }
};

/**
 * 売上合計（全てのPartNoの売上＋全てのPartNoの金型の売上＋全てのPartNoの開発費の売上＋プロジェクト単位の金型の売上＋プロジェクト単位の開発費の売上）の取得(月別)
 *
 * @param {{ year: number, month: number, sales: Decimal }[]} totalSalesByPartNoMonth 全てのPartNoの月別売上
 * @param {{ year: number, month: number, sales: Decimal }[]} totalMoldSalesByMonth   全てのPartNoの金型の月別売上
 * @param {{ year: number, month: number, sales: Decimal }[]} totalDevSalesByMonth    全てのPartNoの開発費の月別売上
 * @param {{ year: number, month: number, sales: Decimal }[]} moldSalesByProjectYear  プロジェクト単位の金型の月別売上
 * @param {{ year: number, month: number, sales: Decimal }[]} devSalesByProjectYear   プロジェクト単位の開発費の月別売上
 * @param {{ year: number, month: number, sales: Decimal }[]} projectEquipmentSalesByMonth プロジェクト単位の設備費の月別売上
 * @returns {Decimal[]} 年度別売上合計
 */
export const getTotalSalesMoldDevByMonth = (
  totalSalesByPartNoMonth: { year: number, month: number, sales: Decimal }[],
  totalMoldSalesByMonth: { year: number, month: number, sales: Decimal }[],
  totalDevSalesByMonth: { year: number, month: number, sales: Decimal }[],
  projectMoldSalesByMonth: { year: number, month: number, sales: Decimal }[],
  projectDevSalesByMonth: { year: number, month: number, sales: Decimal }[],
  projectEquipmentSalesByMonth: { year: number, month: number, sales: Decimal }[],
): { year: number, month: number, sales: Decimal }[] => {
  const functionName = "getTotalSalesByYearTmp";
  try {

    // 各配列を結合
    const combinedSales = [
      ...totalSalesByPartNoMonth,
      ...totalMoldSalesByMonth,
      ...totalDevSalesByMonth,
      ...projectMoldSalesByMonth,
      ...projectDevSalesByMonth,
      ...projectEquipmentSalesByMonth
    ];

    // 集約処理
    const aggregatedSales = combinedSales.reduce<{
      [key: string]: { year: number; month: number; sales: Decimal };
    }>((acc, { year, month, sales }) => {
      // 年と月を識別するキーを作成
      const key = `${year}-${month}`;

      if (!acc[key]) {
        // 初期化
        acc[key] = { year, month, sales: new Decimal(0) };
      }

      // 各売上を合算
      acc[key].sales = acc[key].sales.plus(sales);

      return acc;
    }, {});
    // オブジェクトを配列に変換
    return Object.values(aggregatedSales);

  } catch (error) {
    console.error(`${functionName} ERROR:`, error);
    return [];
  }
};

/**
 * 売上合計の取得(年度別)
 *
 * 売上合計（全てのPartNoの売上＋全てのPartNoの金型の売上＋全てのPartNoの開発費の売上＋プロジェクト単位の金型の売上＋プロジェクト単位の開発費の売上）の取得(年度別)
 *
 * @param {{ year: number, month: number, sales: Decimal }[]} totalSalesByPartNoYear 全てのPartNoの年度別売上
 * @param {{ year: number, month: number, sales: Decimal }[]} totalMoldSalesByYear   全てのPartNoの金型の年度別売上
 * @param {{ year: number, month: number, sales: Decimal }[]} totalDevSalesByYear    全てのPartNoの開発費の年度別売上
 * @param {{ year: number, month: number, sales: Decimal }[]} moldSalesByProjectYear  プロジェクト単位の金型の年度別売上
 * @param {{ year: number, month: number, sales: Decimal }[]} devSalesByProjectYear   プロジェクト単位の開発費の年度別売上
 * @param {{ year: number, month: number, sales: Decimal }[]} equipmentSalesByProjectYear プロジェクト単位の設備費の年度別売上
 * @returns {Decimal[]} 年度別売上合計
 */
export const getTotalSalesMoldDevByYear = (
  totalSalesByPartNoYear: { partNo: string, totalSales: Decimal[] }[],
  totalMoldSalesByYear: { partNo: string, totalSales: Decimal[] }[],
  totalDevSalesByYear: { partNo: string, totalSales: Decimal[] }[],
  moldSalesByProjectYear: Decimal[],
  devSalesByProjectYear: Decimal[],
  equipmentSalesByProjectYear: Decimal[],
): Decimal[] => {
  const functionName = "getTotalSalesMoldDevByYear";
  try {
    // 各配列を結合
    const allSalesArrays = [
      ...totalSalesByPartNoYear.map(item => item.totalSales),
      ...totalMoldSalesByYear.map(item => item.totalSales),
      ...totalDevSalesByYear.map(item => item.totalSales),
      moldSalesByProjectYear,
      devSalesByProjectYear,
      equipmentSalesByProjectYear
    ];

    // 配列が存在する限り、最大の長さを取得
    const maxLength = Math.max(...allSalesArrays.map(salesArray => salesArray.length));

    // インデックスごとに合算
    const totalSalesByIndex = Array.from({ length: maxLength }, (_, i) => {
      return allSalesArrays.reduce((sum, salesArray) => {
        return sum.plus(salesArray[i] || new Decimal(0));
      }, new Decimal(0));
    });
    return totalSalesByIndex;
  } catch (error) {
    console.error(`${functionName} ERROR:`, error);
    return [];
  }
};

/**
 * 売上合計の取得(月別)
 * 
 * @param {{ partNo: string, year: number, totalSales: Decimal[] }[]} totalSalesByPartNoMonth PartNo別売上金額(月別)
 * @returns {{ year: number, month: number, sales: Decimal }[]} 月の売上を合算した配列 [year:1, month:1 sales: 2000]
 */
export const getTotalSalesByMonth = (
  totalSalesByPartNoMonth: { partNo: string, year: number, totalSales: Decimal[] }[],
): { year: number, month: number, sales: Decimal }[] => {
  const functionName = "getTotalSalesByMonth";
  try {
    // 月次売上データを集約するマップ
    const aggregatedSales: Map<string, { year: number, month: number, sales: Decimal }> = new Map();

    // PartNo別データを処理
    totalSalesByPartNoMonth.forEach(({ year, totalSales }) => {
      // 開始月数を取得
      let satrtMonth = 4 + (12 - totalSales.length);
      if (totalSales.length < 4) {
        satrtMonth = satrtMonth - 12;
      }
      totalSales.forEach((sales, monthIndex) => {
        // 現在の月数
        let month = satrtMonth + monthIndex;
        if (month > 12) {
          month = month - 12;
        }
        const key = `${year}-${month}`; // 年度と月をキーとして管理

        if (!aggregatedSales.has(key)) {
          // キーが存在しない場合、新規作成
          aggregatedSales.set(key, { year, month, sales: new Decimal(0) });
        }

        // 売上金額を加算
        const current = aggregatedSales.get(key)!;
        current.sales = current.sales.plus(sales);

        aggregatedSales.set(key, current);
      });
    });

    // 結果を配列で返却
    return Array.from(aggregatedSales.values());
  } catch (error) {
    console.error(`${functionName} ERROR: `, error);
    return [];
  }
};

/**
 * 材料費合計の取得(月別)
 *
 * @param {{ partNo: string, year: number, totalMaterialCosts: Decimal[] }[]} totalMaterialCostsByPartNoMonth PartNo別月別材料費データ
 * @returns {{ year: number, month: number, materialCost: Decimal }[]} 月の材料費を合算した配列
 * 
 */
export const getTotalMaterialCostByMonth = (
  totalMaterialCostsByPartNoMonth: { partNo: string, year: number, totalMaterialCosts: Decimal[] }[],
): { year: number, month: number, materialCost: Decimal }[] => {
  const functionName = "getTotalMaterialCostByMonth";
  try {
    // 月インデックスを変換する関数
    const transformMonthIndex = (index: number, totalLength: number): number => {
      // インデックスを後ろからカウントし「3, 2, 1, 12, 11, ...」に変換
      const reversedIndex = totalLength - index - 1;
      return ((15 - reversedIndex) % 12) || 12;
    };

    // 月次材料費データを集約するマップ
    const aggregatedMaterialCosts: Map<string, { year: number, month: number, materialCost: Decimal }> =
      new Map();

    // PartNo別データを処理
    totalMaterialCostsByPartNoMonth.forEach(({ year, totalMaterialCosts }) => {
      const totalMonths = totalMaterialCosts.length;

      totalMaterialCosts.forEach((materialCost, monthIndex) => {
        const transformedMonth = transformMonthIndex(monthIndex, totalMonths); // 月インデックスを変換
        const key = `${year}-${transformedMonth}`; // 年度と月をキーとして管理

        if (!aggregatedMaterialCosts.has(key)) {
          // キーが存在しない場合、新規作成
          aggregatedMaterialCosts.set(key, { year, month: transformedMonth, materialCost: new Decimal(0) });
        }

        // 材料費金額を加算
        const current = aggregatedMaterialCosts.get(key)!;
        current.materialCost = current.materialCost.plus(materialCost);

        aggregatedMaterialCosts.set(key, current);
      });
    });

    // 結果を配列で返却
    return Array.from(aggregatedMaterialCosts.values());
  } catch (error) {
    console.error(`${functionName} ERROR: `, error);
    return [];
  }
};

/**
 * 材料費合計の取得(年度別)
 *
 * @param {{ year: number, month: number, materialCost: Decimal }[]} totalMaterialCostByMonth 月別材料費データ
 * @returns {Decimal[]} 材料費合計(年度別) (例) ["100000","400000"...]
 */
export const getTotalMaterialCostByYear = (
  totalMaterialCostByMonth: { year: number, month: number, materialCost: Decimal }[]
): Decimal[] => {
  const functionName = "getTotalMaterialCostByYear";
  try {
    // 年度別材料費合計を格納する配列
    const totalMaterialCostByYear: Decimal[] = [];

    totalMaterialCostByMonth.forEach(({ year, materialCost }) => {
      // 年度のインデックスを計算
      const yearIndex = year - 1; // 年度インデックス（例: 2023年 → index 0）

      // 該当する年度の材料費を加算
      totalMaterialCostByYear[yearIndex] = (totalMaterialCostByYear[yearIndex] || new Decimal(0)).plus(materialCost);
    });

    return totalMaterialCostByYear;
  } catch (error) {
    console.error(`${functionName} ERROR:`, error);
    return [];
  }
};

/**
 * NOPATの算出(年度別または月別)
 *
 * @param {number} numberOfYears 表示年数
 * @param {Decimal[]} ebitByYear EBIT
 * @param {Decimal} taxRate 実行税率
 * @param {boolean} hasDecimal 小数有無
 * @returns {Decimal[]} NOPAT(年度別または月別) (例) ["100000","400000"...]
 */
export const calcNopat = (
  numberOfYears: number,
  ebitByYear: Decimal[],
  taxRate: Decimal,
  hasDecimal: boolean,
): Decimal[] => {
  const functionName = "calcNopat";
  try {
    // 返却用変数宣言
    let nopatArray: Decimal[] = [];
    // 税引後率を計算
    const afterTaxRate = new Decimal(100).minus(taxRate);
    // NOPATを取得
    for (let i = 0; i < numberOfYears; i++) {
      // NOPAT = EBIT * (税引後率 / 100)
      const nopat = ebitByYear[i].times(afterTaxRate.div(100));
      // 計算結果を格納
      nopatArray.push(new Decimal(nopat.toFixed(hasDecimal ? 3 : 0)));
    }
    // NOPAT配列を返却
    return nopatArray;
  } catch (error) {
    console.error(`${functionName} ERROR: `, error);
    return [];
  }
}

/**
 * 投下資本の算出(年度別または月別)
 *
 * @param {number} numberOfYears 表示年数
 * @param {Decimal} capexAmount[] 年度毎の設備投資額
 * @param {Decimal[]} depreciationByYear 減価償却費
 * @param {boolean} hasDecimal 小数有無
 * @returns {Decimal[]} 投下資本(年度別または月別) (例) ["100000","400000"...]
 */
export const calcUndepreciated = (
  numberOfYears: number,
  capexAmount: Decimal[],
  depreciationByYear: Decimal[],
  hasDecimal: boolean,
): Decimal[] => {
  const functionName = "calcUndepreciated";
  try {
    // 返却用変数宣言
    let undepreciatedArray: Decimal[] = [];
    // 帳簿価額用変数宣言
    let carryingValue: Decimal = new Decimal(0);

    // 投下資本を取得
    for (let i = 0; i < numberOfYears; i++) {
      // 投下資本(帳簿価額) = 設備投資額(翌年度以降は帳簿価額) - 減価償却費
      carryingValue = carryingValue.plus(capexAmount[i].abs() || new Decimal(0));
      carryingValue = carryingValue.minus(depreciationByYear[i] || new Decimal(0));
      undepreciatedArray.push(new Decimal(carryingValue.toFixed(hasDecimal ? 3 : 0)));
    }
    // 投下資本配列を返却
    return undepreciatedArray;
  } catch (error) {
    console.error(`${functionName} ERROR: `, error);
    return [];
  }
}

/**
 * ROICの算出(年度別または月別)
 *
 * @param {number} numberOfYears 表示年数
 * @param {Decimal[]} nopatByYear NOPAT
 * @param {Decimal[]} undepreciatedByYear 投下資本
 * @returns {Decimal[]} ROIC(年度別または月別) (例) ["100000","400000"...]
 */
export const calcRoic = (
  numberOfYears: number,
  nopatByYear: Decimal[],
  undepreciatedByYear: Decimal[],
): Decimal[] => {
  const functionName = "calcRoic";
  try {
    // 返却用変数宣言
    let roicArray: Decimal[] = [];
    // ROICを取得
    for (let i = 0; i < numberOfYears; i++) {
      // ROIC = (NOPAT / 投下資本) * 100
      // ゼロ資本を考慮して投下資本が0の場合は0で表示
      let roic = new Decimal(0);
      if (Number(undepreciatedByYear[i]) !== 0) {
        roic = nopatByYear[i].div(undepreciatedByYear[i]).times(100);
      }
      // 計算結果を格納
      roicArray.push(new Decimal(roic.toFixed(2)));
    }
    // ROIC配列を返却
    return roicArray;
  } catch (error) {
    console.error(`${functionName} ERROR: `, error);
    return [];
  }
}

/**
 * PVの算出 (年度単位の結果出力)
 *
 * @param {Array<{ year: number; month: number; value: Decimal }>} freeCashFlowByMonth 月別のフリーキャッシュフロー
 * @param {Decimal} discountRate 割引率
 * @param {boolean} hasDecimal 小数有無
 * @returns {Decimal[]} 年度別PVリスト
 */
export const calcPresentValue = (
  freeCashFlowByMonth: { year: number; month: number; value: Decimal }[],
  discountRate: Decimal,
  hasDecimal: boolean
): Decimal[] => {
  const functionName = "calcPresentValue";
  try {
    // 割引率を月次で調整
    const monthlyDiscountRate = discountRate.div(100).div(12);

    // 累積月数を追跡する変数
    let cumulativeMonths = 0;

    // 年度ごとの集計マップを初期化
    const pvByYear: { [year: number]: Decimal } = {};

    // 月別の現在価値を計算
    freeCashFlowByMonth.forEach(({ year, month, value }) => {
      cumulativeMonths++; // 月数を累積増加

      // 現在価値の計算
      const pv = value.div(new Decimal(1).plus(monthlyDiscountRate).pow(cumulativeMonths));

      // 小数点以下の丸め
      const roundedPV = new Decimal(pv.toFixed(hasDecimal ? 3 : 0));

      // 年度ごとに集計
      if (!pvByYear[year]) {
        pvByYear[year] = new Decimal(0);
      }
      pvByYear[year] = pvByYear[year].plus(roundedPV);
    });

    // 結果を年度順に並べて配列で返却
    return Object.keys(pvByYear)
      .map(Number) // キーを数値化
      .sort((a, b) => a - b) // 年度順にソート
      .map((year) => pvByYear[year]); // 値のみを配列化
  } catch (error) {
    console.error(`${functionName} ERROR: `, error);
    return [];
  }
};

/**
 * NPVの算出
 *
 * @param {Decimal[]} pvByYear 年度ごとのPV（現在価値）の配列
 * @param {Decimal} capexAmount 初期投資額（設備投資）
 * @param {boolean} hasDecimal 小数点以下の有無
 * @returns {Decimal[]} 配列の0番目としてNPVを返却
 */
export const calcDiscountedCashFlow = (
  pvByYear: Decimal[],
  capexAmount: Decimal,
  hasDecimal: boolean
): Decimal[] => {
  try {

    // NPVを計算: 合計PV
    const npv = pvByYear.reduce((acc, currentPv) => acc.plus(currentPv), new Decimal(0));

    // 小数点以下の有無を調整して返却
    const formattedNpv = new Decimal(npv.toFixed(hasDecimal ? 3 : 0));

    // 配列の0番目にNPVを設定して返却
    return [formattedNpv];
  } catch (error) {
    console.error("calcDiscountedCashFlow ERROR: ", error);
    return [new Decimal(0)]; // エラーの場合は配列0番目に0を返却
  }
};

/**
 * 設備投資額の算出(年別)
 *
 * @param {{ equipmentId: string, year: number, month: number, capexAmount: Decimal }[][]} capexEquipment 設備情報
 * @param {number} numberOfYears 年数
 * @param {boolean} hasDecimal 小数有無
 * @param {string} projectStartDate プロジェクト開始日
 * @returns {Decimal[]} 設備投資額(年別) (例) ["-100000","0"...]
 */
export const calcCapexAmountByYear = (
  capexEquipment: { equipmentId: string, year: number, month: number, capexAmount: Decimal }[][],
  numberOfYears: number,
  hasDecimal: boolean,
  projectStartDate: string,
): Decimal[] => {
  const functionName = "calcCapexAmountByYear";
  try {
    // 返却用変数宣言
    let capexAmountArray: Decimal[] = Array.from({ length: numberOfYears }, () => new Decimal(0));
    // 初年度を取得
    const startDateObj = dayjs(projectStartDate);
    // const firstFiscalYear = startDateObj.month() - 1 >= 4 ? startDateObj.year() : startDateObj.year() - 1;
    const firstFiscalYear = (startDateObj.month() + 1) >= 4 ? startDateObj.year() : startDateObj.year() - 1;

    capexEquipment.forEach((equip) => {
      for (let i = 0; i < equip.length; i++) {
        // 年度を計算（4月開始: 4〜12月はその年、1〜3月は前年）
        const fiscalYear = equip[i].month >= 4 ? equip[i].year : equip[i].year - 1;
        const yearIndex = fiscalYear - firstFiscalYear;
        // 配列[該当の年度Index]に設備投資額を加算
        capexAmountArray[yearIndex] = capexAmountArray[yearIndex].add(equip[i].capexAmount);
      }
    });
    capexAmountArray = capexAmountArray.map((value) => new Decimal(value.toFixed(hasDecimal ? 3 : 0)));

    // 設備投資額配列を返却
    return capexAmountArray;
  } catch (error) {
    console.error(`${functionName} ERROR: `, error);
    return [];
  }
}

/**
 * 設備投資額の計算
 * @param equipmentData 設備情報
 * @param totalFiscalYears 年度数
 * @param projectStartDate プロジェクト開始日
 * @param isUncollected 未回収フラグ （TRUEの場合は未回収額を返却、FALSEの場合は回収額を返却）
 * @returns 設備投資額（償却開始月に計上）
 */
export const calcCapexAmountByMonth = (
  equipmentData: EquipmentType,
  totalFiscalYears: number,
  projectStartDate: string,
  isUncollected: boolean = true,
): { equipmentId: string, year: number, month: number, capexAmount: Decimal }[] => {

  const functionName = "calcCapexAmountByMonth";
  try {

    // 1. 初期設定
    const capexAmountArray: { equipmentId: string, year: number, month: number, capexAmount: Decimal }[] = [];
    // プロジェクト開始日を取得
    const startDateObj = dayjs(projectStartDate);
    // 初年度の月数を計算
    const startMonth = startDateObj.month() + 1;
    const firstFiscalMonths = startMonth < 4 ? 4 - startMonth : 12 - (startMonth - 4);
    // 年度毎の月数リストを作成
    const monthsPerYear = [firstFiscalMonths, ...Array(totalFiscalYears - 1).fill(12)];
    // 総月数を計算
    const totalMonths = monthsPerYear.reduce((sum, m) => sum + m, 0);
    // 検収月を取得（段階的設備投資で変更されている場合はそちらを優先）
    const effectiveAcceptanceMonth = (equipmentData as any).changedAcceptanceMonth || equipmentData.acceptanceMonth;
    const acceptanceMonth = dayjs(effectiveAcceptanceMonth);
    // 設備投資額の変換
    const capexAmount = new Decimal(equipmentData.amount || 0);
    const adjustedAmount = isUncollected ? capexAmount : capexAmount.negated();
    // 検収月がプロジェクト開始日より前かを判定
    const isAcceptanceMonthBeforeStartDate = acceptanceMonth.isBefore(startDateObj, 'month');

    // 2. 月ごとの設備投資額を計算
    let currentMonth = startDateObj;
    for (let m = 0; m < totalMonths; m++) {
      // 当月の設備投資額を設定
      const currentMonthCapex = (currentMonth.isSame(acceptanceMonth, 'month') || (isAcceptanceMonthBeforeStartDate && m === 0))
        ? adjustedAmount
        : new Decimal(0);

      capexAmountArray.push({
        equipmentId: equipmentData.equipmentId!,
        year: currentMonth.year(),
        month: currentMonth.month() + 1,
        capexAmount: currentMonthCapex,
      });

      currentMonth = currentMonth.add(1, "month");
    }

    // 3. 計算結果を返却
    return capexAmountArray;

  } catch (error) {
    console.error(`${functionName} ERROR: `, error);
    return [];
  }

};

/**
 * 回収/未回収額の計算
 * @param profitArray 営業利益
 * @param capexAmount 設備投資額（償却開始月に計上）
 * @param hasDecimal 小数有無（TRUEの場合は小数あり、FALSEの場合は小数なし）
 * @param isUncollected 未回収フラグ （TRUEの場合は未回収額を返却、FALSEの場合は回収額を返却）
 * @returns 回収/未回収額
 */
export const calcRecoverySimulation = (
  profitArray: Decimal[],
  capexAmount: Decimal[],
  hasDecimal: boolean,
  isUncollected: boolean = true,
): Decimal[] => {

  const functionName = "calcRecoverySimulation";
  try {

    // 1. 設備投資額の変換（回収/未回収に応じて符号変換）
    const cumulativeRecovery: Decimal[] = capexAmount.map((value) =>
      isUncollected ? new Decimal(value.abs()) : new Decimal(value.abs().negated())
    );

    // 2. 回収/未回収額を計算
    const recoverySimulationArray: Decimal[] = cumulativeRecovery.map((_, count) => {
      const previousAmount = count > 0 ? cumulativeRecovery[count - 1] : new Decimal(0);
      const currentProfit = profitArray[count] || new Decimal(0);

      cumulativeRecovery[count] = isUncollected
        ? cumulativeRecovery[count].plus(previousAmount).minus(currentProfit)
        : cumulativeRecovery[count].plus(previousAmount).plus(currentProfit);

      // 各計算を小数点以下処理しつつ保存
      return new Decimal(cumulativeRecovery[count].toFixed(hasDecimal ? 3 : 0));
    });

    // 3. 計算結果を返却
    return recoverySimulationArray;

  } catch (error) {
    console.error(`${functionName} ERROR: `, error);
    return [];
  }

}

/**
 * 金利の計算
 * @param capexAmountByYear 設備投資額
 * @param depreciationByYear 減価償却費
 * @param baseInterestRate 基準金利(％)
 * @returns 金利(年単位)
 */
export const calcInterestRateByYear = (capexAmountByYear: Decimal[], depreciationByYear: Decimal[], baseInterestRate: Decimal) => {

  let cumulativeAmount = new Decimal(0);
  let cumulativeDepreciation = new Decimal(0);
  const result = [];

  const functionName = "calcInterestRateByYear";

  try {

    if (baseInterestRate.equals(0)) {
      // 基準金利が未定義の場合
      console.warn(`${functionName} baseInterestRate is Zero.(Not defined.)`);
      return [];
    }

    for (let i = 0; i < capexAmountByYear.length; i++) {
      // 設備投資金額(累積)
      cumulativeAmount = cumulativeAmount.plus(capexAmountByYear[i]);
      // 減価償却費(累積)
      cumulativeDepreciation = cumulativeDepreciation.plus(depreciationByYear[i]);

      // 金利計算：(設備投資金額(累積) - 減価償却費(累積)) × 基準金利(%なので換算)
      const interestRate = cumulativeAmount.minus(cumulativeDepreciation).mul(baseInterestRate).div(100);

      result.push(interestRate);
    }

    return result;
  } catch (error) {
    console.error(`${functionName} ERROR:`, error);
    throw [];
  }
}

/**
 * string型配列に変換
 * @param decimalArray 変換したいDecimal型配列
 * @param totalFiscalYears 年度数（引数なしの場合も対応）
 * @returns string型配列
 */
export const convertToStringArray = (
  decimalArray: Decimal[],
  totalFiscalYears?: number
): string[] => {
  const functionName = "convertToStringArray";
  try {

    // string配列の長さを決定
    const length = totalFiscalYears ?? decimalArray.length;

    // Decimalをstringに変換（不足部分はデフォルト値）
    return Array.from({ length }, (_, index) =>
      decimalArray[index]?.toString() ?? new Decimal(0).toString()
    );

  } catch (error) {
    console.error(`${functionName} ERROR:`, error);
    throw error;
  }
};

/**
 * 時間(秒)に変換
 *
 * @param {string} timeMinute 時間(分)
 * @param {number} decimalPoint 小数点位置
 * @returns {string} 時間(秒)
 */
export const convertToSecond = (
  timeMinute: string,
  decimalPoint?: number,
): string | null => {
  const functionName = "convertToSecond";
  try {
    // 時間(分)を時間(秒)に変換
    const result = (Number(timeMinute) * 60);
    return decimalPoint ? result.toFixed(decimalPoint) : result.toString();
  } catch (error) {
    console.error(`${functionName} ERROR: `, error);
    throw error;
  }
}

/**
 * 指定した小数点位置で丸めた数値文字列を返す。
 * @param value 数値文字列
 * @param decimalPoint 小数点位置
 * @returns 小数点位置で丸めた数値文字列
 */
export const stringToFixed = (value: string, decimalPoint: number) => {
  if (!Number.isInteger(decimalPoint) || decimalPoint < 0) {
    throw new Error(`Invalid digit count: ${decimalPoint}`);
  }

  try {
    return new Decimal(value || "0").toFixed(decimalPoint);
  } catch {
    return "0";
  }
}

/**
 * 設備データを金額為替変換し返却
 *
 * @param {EquipmentType[]} equipmentData - 元の設備データ
 * @param {string} targetCurrency - 表示通貨
 * @param {CurrencyData[]} currencyData - 通貨データ
 * @returns {EquipmentType[]} 為替変換済みの設備データ
 */
export const convertEquipmentData = (
  equipmentData: EquipmentType[],
  targetCurrency: string,
  currencyData: Currency[],
  convertFn?: ConvertFn
): EquipmentType[] => {
  const functionName = "convertEquipmentData";
  try {
    const convert = convertFn
      || ((amount: number | Decimal, from: string, to: string) => convertCurrency(amount, from, to, currencyData));
    console.log(`${functionName} START`);

    // 為替変換を適用した設備データ
    const convertedEquipmentData = equipmentData.map((item) => {
      // 金額、単価、通貨IDが不足している場合はそのまま返却
      if (!item.amount || !item.unitPrice || !item.currencyId) {
        return item;
      }

      // 設備の通貨データを検索
      const equipmentCurrencyData = currencyData.find((currency) => currency.currencyId === item.currencyId);

      // 通貨名が見つからない場合はそのまま返却
      if (!equipmentCurrencyData?.currencyName) {
        return item;
      }

      // 金額(Decimal型)の作成と為替変換
      // 設備の検収月（acceptanceMonth）のレートで変換（振り返りモード時）
      const unitPrice = new Decimal(item.unitPrice);
      const amount = new Decimal(item.amount);
      const yearMonth = item.acceptanceMonth?.substring(0, 7); // "YYYY-MM-DD" → "YYYY-MM" or undefined
      const convertedUnitPrice = convert(
        unitPrice,
        equipmentCurrencyData.currencyName,
        targetCurrency,
        yearMonth
      ).toDecimalPlaces(2, Decimal.ROUND_DOWN);

      const convertedAmount = convert(
        amount,
        equipmentCurrencyData.currencyName,
        targetCurrency,
        yearMonth
      );

      // 変換先通貨の情報を取得
      const targetCurrencyData = currencyData.find(c => c.currencyName === targetCurrency);

      return {
        ...item,
        unitPrice: convertedUnitPrice.toNumber(),
        amount: convertedAmount.toString(),
        // 変換後の通貨情報を更新（2重変換を防ぐ）
        currencyId: targetCurrencyData?.currencyId || item.currencyId,
        currencyName: targetCurrency,
      };
    });

    // 結果を返却（為替変換済みデータ）
    return convertedEquipmentData;

  } catch (error) {
    console.error(`${functionName} ERROR: `, error);
    return [];
  } finally {
    console.log(`${functionName} END`);
  }
};

/**
 *
 * @param equipmentData 設備情報
 * @param optionFunc オプション処理（ついでにやりたい処理）
 * @returns
 */
export const calcEquipmentQuantity = (equipmentData: EquipmentExtType[], optionFunc?: (e: EquipmentExtType) => void) => {
  if (!equipmentData) {
    return [];
  }

  for (const e of equipmentData) {
    // 数量がそのまま増減数
    e.changeInQuantity = e.quantity || 0;

    if (optionFunc) {
      // ついでにやりたい処理
      optionFunc(e);
    }
  }

  return equipmentData;
}

// 共通部品: プロジェクト単位の金型または開発費売上合計の計算
/**
 * （数量・単価）プロジェクト単位金型またはプロジェクト単位開発費データの月毎の金額を取得する
 * 各テーブルに設定されたcurrencyIdを使用して通貨変換を行う
 *
 * @param {string} projectStartDate プロジェクト開始日
 * @param {string} salesByProject プロジェクト単位金型またはプロジェクト単位開発費データ
 * @param {string} localCurrency ローカル通貨名
 * @param {currencyData} currencyData 通貨情報
 * @returns {{ year: number, month: number, sales: Decimal }[]} 各年度の月毎の金額を格納して返却
 */
export const getProjectSalesByMonth = (
  projectStartDate: string,                                     // プロジェクト開始日
  salesByProject: string | null | undefined,                    // 金型または開発費データ
  localCurrency: string,                                        // ローカル通貨名
  currencyData: Currency[],                                     // 通貨情報
  convertFn?: ConvertFn
): { year: number, month: number, sales: Decimal }[] => {
  const functionName = "getProjectSalesByMonth";
  const convert = convertFn
    || ((amount: number | Decimal, from: string, to: string) => convertCurrency(amount, from, to, currencyData));
  // 各年度の合計金額を格納する配列
  const salesByProjectMonth: { year: number, month: number, sales: Decimal }[] = [];

  try {
    console.log(`${functionName} START`);

    let salesByProjectArray: any[] = [];
    salesByProjectArray = salesByProject ? JSON.parse(salesByProject) : [];

    // プロジェクト開始月を取得
    const startMonth = projectStartDate
      ? parseInt(projectStartDate.split("-")[1], 10)
      : 0;
    const startYear = projectStartDate
      ? parseInt(projectStartDate.split("-")[0], 10)
      : 0;

    // データ処理
    salesByProjectArray.forEach((table) => {
      // テーブルごとに販売通貨を取得
      const tableCurrencyId = table.currencyId;
      let salesCurrencyName = "";

      if (tableCurrencyId && currencyData) {
        const currency = currencyData.find(c => c.currencyId === tableCurrencyId);
        salesCurrencyName = currency?.currencyName || "";
      }

      table.years.forEach((year: number) => {
        // 該当年度のデータを取得
        const yearData = table.data[year];
        if (!yearData || !yearData.months) return; // 年度のデータがない場合はスキップ

        // 初年度はプロジェクト開始月以降のデータのみ利用
        const visibleMonthKeys = getVisibleMonths(year, yearData.months, startMonth || 0);

        for (const key in yearData.months) {
          if (Object.prototype.hasOwnProperty.call(yearData.months, key)) {
            const numericKey = Number(key);
            // 初年度表示対象外月の場合スキップ
            if (year === 1 && !visibleMonthKeys.includes(numericKey)) continue;
            const value = yearData.months[numericKey];

            // カレンダー年月を算出
            const calMonth = value.month;
            const calYear = startMonth >= 4
              ? startYear + (year - 1) + (calMonth >= 1 && calMonth <= 3 ? 1 : 0)
              : startYear + (year - 2) + (calMonth >= 4 ? 0 : 1);
            const yearMonth = `${calYear}-${String(calMonth).padStart(2, "0")}`;

            // テーブルごとの販売通貨を使用して変換
            const convertedMonthSales = salesCurrencyName
              ? convert(
                value.price,
                salesCurrencyName,
                localCurrency,
                yearMonth
              )
              : new Decimal(value.price);

            // 各PartNoの月ごとの金型売上を配列形式に変換して返却
            salesByProjectMonth.push({
              year: year,
              month: value.month,
              sales: convertedMonthSales,
            });
          }
        }
      });
    });
    // 各年度の合計金額を返却
    return salesByProjectMonth;
  } catch (error) {
    console.error(`${functionName} ERROR: `, error);
    return [];
  } finally {
    console.log(`${functionName} END`);
  }
};

/**
 * （数量・単価）プロジェクト単位金型またはプロジェクト単位開発費データの年度金額を計算する
 *
 * @param {string} dataByProject プロジェクト単位金型またはプロジェクト単位開発費データ
 * @param {boolean} decimalCurrency 小数有無（小数点第二位を制御するかどうか）
 * @returns {Decimal[]} 各年度の金額を配列に格納して返却
 */
export const calculateProjectSalesSum = (
  dataByProject: { year: number, month: number, sales: Decimal }[], // 金型または開発費データ
  decimalCurrency: boolean // 小数有無
): Decimal[] => {
  const functionName = "calculateProjectSalesSum";
  try {
    console.log(`${functionName} START`);

    // 年ごとの合計売上を計算し、Decimal[] に格納
    const totalSalesByYear: Decimal[] = Object.values(
      dataByProject.reduce<{ [year: number]: Decimal }>((acc, { year, sales }) => {
        if (!acc[year]) {
          acc[year] = new Decimal(0); // 初期値
        }
        // 売上配列の合計を計算し累積
        acc[year] = acc[year].plus(sales);
        return acc;
      }, {})
    );
    const totalSalesByYearRounded = totalSalesByYear.map(decimal => decimal.toDecimalPlaces(decimalCurrency ? 3 : 0));
    return totalSalesByYearRounded;
  } catch (error) {
    console.error(`${functionName} ERROR: `, error);
    return [];
  } finally {
    console.log(`${functionName} END`);
  }
};

/**
 * PartNoの月別金型売上を取得する
 *
 * @param {string} projectStartDate プロジェクト開始日
 * @param {PartNo[]} partNoData PartNo配列データ
 * @param {string} sourceCurrencyName 変換元通貨名
 * @param {string} targetCurrencyName 変換先通貨名
 * @param {Currency[]} currencyData 通貨情報
 * @returns {{ partNo: string, year: number, totalSales: Decimal[] }[] } 各PartNoの月別金型売上合計
 */
export const calcMoldSalesByPartNoMonth = (
  projectStartDate: string,                      // プロジェクト開始日
  partNoData: PartNo[],                          // PartNoデータ配列
  targetCurrencyName: string,                    // 変換後通貨名
  currencyData: Currency[],                      // 通貨データ
  convertFn?: ConvertFn
): { partNo: string, year: number, totalSales: Decimal[] }[] => {
  const functionName = "calcMoldSalesByPartNoMonth";
  try {
    console.log(`${functionName} START`);
    const convert = convertFn
      || ((amount: number | Decimal, from: string, to: string) => convertCurrency(amount, from, to, currencyData));
    const moldSalesByMonthArray: { partNo: string, year: number, totalSales: Decimal[] }[] = [];

    // プロジェクト開始月を取得
    const startMonth = projectStartDate
      ? parseInt(projectStartDate.split("-")[1], 10)
      : 0;
    const startYear = projectStartDate
      ? parseInt(projectStartDate.split("-")[0], 10)
      : 0;

    // 各 PartNo を処理
    partNoData.forEach((part) => {
      const partNo = part.partNo;
      // PartNo が存在しない場合スキップ
      if (!partNo) return;

      // PartNo単位で販売通貨を取得
      const quantityAndPriceData = part.quantityAndPrice ? JSON.parse(part.quantityAndPrice) : null;
      // currencyAndRatioはJSON文字列なのでパースする
      let currencyAndRatio: { id: string; ratio: number }[] = [];
      if (quantityAndPriceData?.currencyAndRatio) {
        try {
          currencyAndRatio = typeof quantityAndPriceData.currencyAndRatio === 'string'
            ? JSON.parse(quantityAndPriceData.currencyAndRatio)
            : quantityAndPriceData.currencyAndRatio;
        } catch (error) {
          console.error(`[${functionName}] Failed to parse currencyAndRatio for PartNo ${partNo}:`, error);
        }
      }

      let sourceCurrencyName = "";

      if (currencyAndRatio.length > 0) {
        // 最大比率を持つ通貨を販売通貨として使用
        const maxRatio = Math.max(...currencyAndRatio.map(c => c.ratio));
        const firstMaxIndex = currencyAndRatio.findIndex(c => c.ratio === maxRatio);
        const salesCurrencyId = currencyAndRatio[firstMaxIndex]?.id;

        if (salesCurrencyId && currencyData) {
          const currency = currencyData.find(c => c.currencyId === salesCurrencyId);
          sourceCurrencyName = currency?.currencyName || "";
        }
      }

      // moldSalesByPartNo データをパース
      const moldSalesByPartNoStr = part.moldSalesByPartNo;
      // 金型売上データがない場合スキップ
      if (!moldSalesByPartNoStr) return;

      let moldSalesByPartNo: MoldMonthTable[];
      try {
        moldSalesByPartNo = JSON.parse(moldSalesByPartNoStr) as MoldMonthTable[];
      } catch (error) {
        console.error(`Error parsing moldSalesByPartNo for PartNo ${partNo}:`, error);
        // JSON 解析エラー時スキップ
        return;
      }

      // moldSalesByPartNo を処理
      moldSalesByPartNo.forEach((moldTable) => {
        moldTable.years.forEach((year) => {
          const salesArray: Decimal[] = [];
          const yearData = moldTable.data[year];
          if (!yearData || !yearData.months) return;

          // 初年度はプロジェクト開始月以降のデータのみ利用
          const visibleMonthKeys = getVisibleMonths(year, yearData.months, startMonth || 0);

          // 配列位置カウンタ（getTotalSalesByMonth が index 0 を startMonth として解釈するため）
          let arrayIndex = 0;
          for (const key in yearData.months) {
            if (Object.prototype.hasOwnProperty.call(yearData.months, key)) {
              const numericKey = Number(key);
              // 初年度表示対象外月の場合スキップ
              if (year === 1 && !visibleMonthKeys.includes(numericKey)) continue;
              const value = yearData.months[numericKey];
              // カレンダー年月を算出（配列位置ベース）
              const fiscalMonth = startMonth + arrayIndex;
              const actualMonth = fiscalMonth > 12 ? fiscalMonth - 12 : fiscalMonth;
              const calYear = startMonth >= 4
                ? startYear + (year - 1) + (actualMonth >= 1 && actualMonth <= 3 ? 1 : 0)
                : startYear + (year - 2) + (actualMonth >= 4 ? 0 : 1);
              const yearMonth = `${calYear}-${String(actualMonth).padStart(2, "0")}`;
              const convertedMonthSales = sourceCurrencyName
                ? convert(
                  value.price,
                  sourceCurrencyName,
                  targetCurrencyName,
                  yearMonth
                )
                : new Decimal(value.price);
              salesArray.push(convertedMonthSales);
              arrayIndex++;
            }
          }
          // 各PartNoの月ごとの金型売上を配列形式に変換して返却
          moldSalesByMonthArray.push({
            partNo: partNo,
            year: year,
            totalSales: salesArray,
          });
        });
      });
    });
    return moldSalesByMonthArray;
  } catch (error) {
    console.error(`${functionName} ERROR: `, error);
    return [];
  } finally {
    console.log(`${functionName} END`);
  }
};

/**
 * 全てのPartNoの売上を年度毎に合算する
 *
 * @param {{ partNo: string, totalSales: Decimal[] }[]} partNoSales 各PartNoの年度売上配列
 * @returns {Decimal[]} 全PartNoの年度別売上合計
 */
export const calcTotalPartNoSalesByYear = (
  partNoSales: { partNo: string, totalSales: Decimal[] }[]
): Decimal[] => {
  const functionName = "calcTotalPartNoSalesByYear";
  try {
    console.log(`${functionName} START`);

    // 最大年度数を求める
    const maxYears = Math.max(
      ...partNoSales.map((entry) => entry.totalSales.length)
    );

    // 全 PartNo の年度売上を合算
    const yearlyTotals: Decimal[] = Array.from({ length: maxYears }, (_, yearIndex) => {
      return partNoSales.reduce((sum, entry) => {
        // 該当年度の値を取得（存在しない場合は0）
        const value = entry.totalSales[yearIndex] || new Decimal(0);
        return sum.plus(value);
      }, new Decimal(0));
    });

    return yearlyTotals;
  } catch (error) {
    console.error(`${functionName} ERROR:`, error);
    return [];
  } finally {
    console.log(`${functionName} END`);
  }
};


/**
 * 全PartNoの金型売上を年度毎に合算する
 *
 * @param {{ partNo: string, totalSales: Decimal[] }[]} partNoSales 各PartNoの年度金型売上配列
 * @returns {Decimal[]} 全PartNoの年度別金型売上合計
 */
export const calcTotalMoldSalesByYear = (
  partNoSales: { partNo: string, totalSales: Decimal[] }[]
): Decimal[] => {
  const functionName = "calcTotalMoldSalesByYear";
  try {
    console.log(`${functionName} START`);

    // 最大年度数を求める
    const maxYears = Math.max(
      ...partNoSales.map((entry) => entry.totalSales.length)
    );

    // 全 PartNo の年度金型売上を合算
    const yearlyTotals: Decimal[] = Array.from({ length: maxYears }, (_, yearIndex) => {
      return partNoSales.reduce((sum, entry) => {
        // 各PartNoの該当年度の値を取り出す（存在しない場合は0）
        const value = entry.totalSales[yearIndex] || new Decimal(0);
        return sum.plus(value);
      }, new Decimal(0));
    });

    return yearlyTotals;
  } catch (error) {
    console.error(`${functionName} ERROR:`, error);
    return [];
  } finally {
    console.log(`${functionName} END`);
  }
};

/**
 * PartNoの月別開発費売上を取得する
 *
 * @param {string} projectStartDate プロジェクト開始日
 * @param {PartNo[]} partNoData PartNo配列データ
 * @param {string} sourceCurrencyName 変換元通貨名
 * @param {string} targetCurrencyName 変換先通貨名
 * @param {Currency[]} currencyData 通貨情報
 * @returns {{ partNo: string, year: number, totalSales: Decimal[] }[] } 各PartNoの月別開発費売上合計
 */
export const calcDevSalesByPartNoMonth = (
  projectStartDate: string,                      // プロジェクト開始日
  partNoData: PartNo[],                          // PartNoデータ配列
  targetCurrencyName: string,                    // 変換後通貨名
  currencyData: Currency[],                      // 通貨データ
  convertFn?: ConvertFn
): { partNo: string, year: number, totalSales: Decimal[] }[] => {
  const functionName = "calcDevSalesByPartNoMonth";
  try {
    console.log(`${functionName} START`);
    const convert = convertFn
      || ((amount: number | Decimal, from: string, to: string) => convertCurrency(amount, from, to, currencyData));
    const devSalesByMonthArray: { partNo: string, year: number, totalSales: Decimal[] }[] = [];

    // プロジェクト開始月を取得
    const startMonth = projectStartDate
      ? parseInt(projectStartDate.split("-")[1], 10)
      : 0;
    const startYear = projectStartDate
      ? parseInt(projectStartDate.split("-")[0], 10)
      : 0;

    // 各 PartNo を処理
    partNoData.forEach((part) => {
      const partNo = part.partNo;
      // PartNo が存在しない場合スキップ
      if (!partNo) return;

      // PartNo単位で販売通貨を取得
      const quantityAndPriceData = part.quantityAndPrice ? JSON.parse(part.quantityAndPrice) : null;
      // currencyAndRatioはJSON文字列なのでパースする
      let currencyAndRatio: { id: string; ratio: number }[] = [];
      if (quantityAndPriceData?.currencyAndRatio) {
        try {
          currencyAndRatio = typeof quantityAndPriceData.currencyAndRatio === 'string'
            ? JSON.parse(quantityAndPriceData.currencyAndRatio)
            : quantityAndPriceData.currencyAndRatio;
        } catch (error) {
          console.error(`[${functionName}] Failed to parse currencyAndRatio for PartNo ${partNo}:`, error);
        }
      }

      let sourceCurrencyName = "";

      if (currencyAndRatio.length > 0) {
        // 最大比率を持つ通貨を販売通貨として使用
        const maxRatio = Math.max(...currencyAndRatio.map(c => c.ratio));
        const firstMaxIndex = currencyAndRatio.findIndex(c => c.ratio === maxRatio);
        const salesCurrencyId = currencyAndRatio[firstMaxIndex]?.id;

        if (salesCurrencyId && currencyData) {
          const currency = currencyData.find(c => c.currencyId === salesCurrencyId);
          sourceCurrencyName = currency?.currencyName || "";
        }
      }

      // developmentSalesByPartNo データをパース
      const developmentSalesByPartNoStr = part.developmentSalesByPartNo;
      // 金型売上データがない場合スキップ
      if (!developmentSalesByPartNoStr) return;

      let devSalesByPartNo: DevelopmentMonthCostTable[];
      try {
        devSalesByPartNo = JSON.parse(developmentSalesByPartNoStr) as DevelopmentMonthCostTable[];
      } catch (error) {
        console.error(`Error parsing developmentSalesByPartNo for PartNo ${partNo}:`, error);
        // JSON 解析エラー時スキップ
        return;
      }

      // moldSalesByPartNo を処理
      devSalesByPartNo.forEach((devTable) => {
        devTable.years.forEach((year) => {
          const salesArray: Decimal[] = [];
          const yearData = devTable.data[year];
          if (!yearData || !yearData.months) return;

          // 初年度はプロジェクト開始月以降のデータのみ利用
          const visibleMonthKeys = getVisibleMonths(year, yearData.months, startMonth || 0);

          // 配列位置カウンタ（getTotalSalesByMonth が index 0 を startMonth として解釈するため）
          let arrayIndex = 0;
          for (const key in yearData.months) {
            if (Object.prototype.hasOwnProperty.call(yearData.months, key)) {
              const numericKey = Number(key);
              // 初年度表示対象外月の場合スキップ
              if (year === 1 && !visibleMonthKeys.includes(numericKey)) continue;
              const value = yearData.months[numericKey];
              // カレンダー年月を算出（配列位置ベース）
              const fiscalMonth = startMonth + arrayIndex;
              const actualMonth = fiscalMonth > 12 ? fiscalMonth - 12 : fiscalMonth;
              const calYear = startMonth >= 4
                ? startYear + (year - 1) + (actualMonth >= 1 && actualMonth <= 3 ? 1 : 0)
                : startYear + (year - 2) + (actualMonth >= 4 ? 0 : 1);
              const yearMonth = `${calYear}-${String(actualMonth).padStart(2, "0")}`;
              const convertedMonthSales = sourceCurrencyName
                ? convert(
                  value.price,
                  sourceCurrencyName,
                  targetCurrencyName,
                  yearMonth
                )
                : new Decimal(value.price);
              salesArray.push(convertedMonthSales);
              arrayIndex++;
            }
          }
          // 各PartNoの月ごとの開発費売上を配列形式に変換して返却
          devSalesByMonthArray.push({
            partNo: partNo,
            year: year,
            totalSales: salesArray,
          });
        });
      });
    });
    return devSalesByMonthArray;
  } catch (error) {
    console.error(`${functionName} ERROR: `, error);
    return [];
  } finally {
    console.log(`${functionName} END`);
  }
};

/**
 * 全てのPartNoの開発費売上を年度毎に合算する
 *
 * @param {{ partNo: string, totalSales: Decimal[] }[]} partNoSales 各PartNoの年度開発費売上配列
 * @returns {Decimal[]} 全PartNoの年度別開発費売上合計
 */
export const calcTotalDevSalesByYear = (
  partNoSales: { partNo: string, totalSales: Decimal[] }[]
): Decimal[] => {
  const functionName = "calcTotalDevSalesByYear";
  try {
    console.log(`${functionName} START`);

    // 最大年度数を求める
    const maxYears = Math.max(
      ...partNoSales.map((entry) => entry.totalSales.length)
    );

    // 全 PartNo の年度開発費売上を合算
    const yearlyTotals: Decimal[] = Array.from({ length: maxYears }, (_, yearIndex) => {
      return partNoSales.reduce((sum, entry) => {
        // 該当年度の値を取得（存在しない場合は0）
        const value = entry.totalSales[yearIndex] || new Decimal(0);
        return sum.plus(value);
      }, new Decimal(0));
    });

    return yearlyTotals;
  } catch (error) {
    console.error(`${functionName} ERROR:`, error);
    return [];
  } finally {
    console.log(`${functionName} END`);
  }
};

/**
 * PartNoごとの年度別合計(製品売上高、金型、開発費)売上を算出
 *
 * @param {{ partNo: string, totalSales: Decimal[] }[]} totalSalesByPartNoYear 年度別売上合計
 * @param {{ partNo: string, totalSales: Decimal[] }[]} moldSalesPartNoYear 年度別金型売上合計
 * @param {{ partNo: string, totalSales: Decimal[] }[]} devSalesPartNoYear 年度別開発費売上合計
 * @returns {{ partNo: string, totalSales: Decimal[] }[]} PartNoごとの年度別合計金額
 */
export const calcPartNoSalesMergeByYear = (
  totalSalesByPartNoYear: { partNo: string, totalSales: Decimal[] }[],
  moldSalesPartNoYear: { partNo: string, totalSales: Decimal[] }[],
  devSalesPartNoYear: { partNo: string, totalSales: Decimal[] }[]
): { partNo: string, totalSales: Decimal[] }[] => {
  const functionName = "calcPartNoSalesMergeByYear";
  try {
    console.log(`${functionName} START`);

    const combinedResults: Record<string, Decimal[]> = {};

    // 全てのデータセットを一つのリストにまとめる
    const allResults = [totalSalesByPartNoYear, moldSalesPartNoYear, devSalesPartNoYear];

    // 各データセットを取り込み処理
    allResults.forEach(resultSet => {
      resultSet.forEach(({ partNo, totalSales }) => {
        // PartNoが存在しなければ初期化
        if (!combinedResults[partNo]) {
          combinedResults[partNo] = [];
        }

        // 各年度ごとの単価を合算
        totalSales.forEach((totalSales, yearIndex) => {
          if (!combinedResults[partNo][yearIndex]) {
            combinedResults[partNo][yearIndex] = new Decimal(0);
          }
          combinedResults[partNo][yearIndex] = combinedResults[partNo][yearIndex].plus(totalSales.toDecimalPlaces(2));
        });
      });
    });

    // 合算結果を配列形式に変換
    const result = Object.entries(combinedResults).map(([partNo, totalSales]) => ({
      partNo,
      totalSales
    }));

    return result;
  } catch (error) {
    console.error(`${functionName} ERROR: `, error);
    return [];
  } finally {
    console.log(`${functionName} END`);
  }
};

/**
 * チャートデータを為替変換する関数
 * @param chartData 売上データ構造
 * @param sourceCurrency 元の通貨コード (例: "JPY")
 * @param targetCurrency 変換先の通貨コード (例: "USD")
 * @param currencyData 通貨データ配列
 * @param decimalCurrency 小数有無（小数点第二位を制御するかどうか）
 * @returns 変換後の売上チャートデータ
 */
export function convertChartData(
  chartData: any,
  sourceCurrency: string,
  targetCurrency: string,
  currencyData: Currency[],
  decimalCurrency: boolean,
): any {
  // 損益非表示モード等でチャートデータが未生成（datasetsを持たない不正な形）の場合は変換不要のためそのまま返す
  if (!chartData || !Array.isArray(chartData.datasets)) {
    return chartData;
  }

  // データセットごとに変換処理
  const updatedDatasets = chartData.datasets.map((dataset: any) => {
    if (dataset.id === "profitRate") {
      // 営業利益率は対象外
      return dataset;
    }

    const convertedData = dataset.data.map((value: string) => {
      const numericValue = Number(value);
      if (isNaN(numericValue)) {
        console.error(`無効な値: ${value}は数値に変換できません`);
        return 0;
      }
      // 同一通貨の場合は丸めを行わず元の値を返す
      if (sourceCurrency === targetCurrency) {
        return value;
      }
      const convertedValue = convertCurrency(numericValue, sourceCurrency, targetCurrency, currencyData);
      return convertedValue.toFixed(decimalCurrency ? 3 : 0);
    });

    return {
      ...dataset,
      data: convertedData,
    };
  });

  // No.411対応（8章）: residualDepreciationTotal（表示期間外の残存減価償却費合計）は
  // datasets配列の外側に付与されるトップレベルフィールドのためdatasets変換処理の対象外となり、
  // 元の通貨（sourceCurrency）のまま残ってしまう。calcTotalProfitAndRateでprofit（変換後）と
  // 単純加算されるため、ここで同様に変換して通貨単位を一致させる。
  const updatedResidualDepreciationTotal = chartData.residualDepreciationTotal != null
    ? (sourceCurrency === targetCurrency
      ? chartData.residualDepreciationTotal
      : convertCurrency(Number(chartData.residualDepreciationTotal), sourceCurrency, targetCurrency, currencyData).toFixed(decimalCurrency ? 2 : 0))
    : chartData.residualDepreciationTotal;

  return {
    ...chartData,
    datasets: updatedDatasets,
    residualDepreciationTotal: updatedResidualDepreciationTotal,
  };
}

/**
 * 営業利益率の算出
 *
 * @param {Decimal} sales 売上合計
 * @param {Decimal} profit 営業利益
 * @returns {Decimal} 営業利益率
 */
export const calcProfitRate = (
  sales: Decimal,
  profit: Decimal,
): Decimal => {
  const functionName = "calcProfitRate";
  try {
    // 売上がゼロ以下の場合は営業利益率0%を返却
    if (sales.lte(0)) return new Decimal(0);
    // 営業利益率(%) = 営業利益 / 売上合計 * 100
    const profitRate = profit.div(sales).times(100);
    return profitRate;
  } catch (error) {
    console.error(`${functionName} ERROR: `, error);
    return new Decimal(0);
  }
}

/**
 * 営業利益率の算出（配列）
 *
 * @param {Decimal[]} salesArray 売上合計（配列）
 * @param {Decimal[]} profitArray 営業利益（配列）
 * @returns {Decimal[]} 営業利益率（配列）
 */
export const calcProfitRateArray = (
  salesArray: Decimal[],
  profitArray: Decimal[],
): Decimal[] => {
  const functionName = "calcProfitRateArray";
  try {
    return salesArray.map((value, index) => {
      const sales = value ?? new Decimal(0);
      const profit = profitArray[index] ?? new Decimal(0);
      if (sales.lte(0)) {
        // 売上がゼロ以下の場合は営業利益率0%を返却
        return new Decimal(0);
      }
      // 営業利益率(%) = 営業利益 / 売上合計 * 100
      const profitRate = profit.div(sales).times(100);
      return profitRate;
    });
  } catch (error) {
    console.error(`${functionName} ERROR: `, error);
    return [];
  }
}

/**
 * スループット率の算出
 * @param { { partNo: string, throughputs: Decimal[] }[]} throughputsByPartNo スループット額
 * @param { { partNo: string, totalSales: Decimal[] }[]} mergePartNoSalesByYear 売上合計
 * @returns {Decimal[]} スループット率
 */
export const calcThroughputRate = (
  throughputsByPartNo: { partNo: string, throughputs: Decimal[] }[],
  mergePartNoSalesByYear: { partNo: string, totalSales: Decimal[] }[],
): { partNo: string, throughputRate: Decimal[] }[] => {
  const functionName = "calcThroughputRate";
  try {
    // 返却用データ
    const results: {
      partNo: string;
      throughputRate: Decimal[];
    }[] = [];
    // throughputsByPartNo をループ処理
    throughputsByPartNo.forEach((throughputItem) => {
      const { partNo, throughputs } = throughputItem;
      // mergePartNoSalesByYear から同じ PartNo の totalSales を取得
      const salesItem = mergePartNoSalesByYear.find(
        (item) => item.partNo === partNo
      );
      if (salesItem) {
        // totalSales を取得
        const totalSales = salesItem.totalSales;
        // throughputs と totalSales の対応する年ごとの計算
        const throughputRate = throughputs.map((throughput, index) => {
          const sales = totalSales[index] || new Decimal(0); // 対応する year の売上が存在するかチェック
          return sales.gt(0) // 売上が 0 より大きいかどうか
            ? new Decimal(throughput.div(sales).times(100).toFixed(1)) // throughputs / totalSales * 100
            : new Decimal(0); // 売上が 0 以下の場合は 0%
        });
        // 結果を追加
        results.push({
          partNo,
          throughputRate: throughputRate,
        });
      }
    });
    return results;
  } catch (error) {
    console.error(`${functionName} ERROR: `, error);
    return [];
  }
};

/**
 * 製品売上の算出（月別）
 *
 * @param {{ year: number, month: number, sales: Decimal }[]} totalSalesByPartNoMonth 全てのPartNoの売上金額
 * @param {{ year: number, month: number, sales: Decimal }[]} totalMoldSalesByMonth   全てのPartNoの金型の売上金額
 * @param {{ year: number, month: number, sales: Decimal }[]} totalDevSalesByMonth    全てのPartNoの開発費の売上金額
 * @returns {{ year: number, month: number, sales: Decimal }[]} 製品売上
 */
export const calcProductSalesByMonth = (
  totalSalesByPartNoMonth: { year: number, month: number, sales: Decimal }[],
  totalMoldSalesByMonth: { year: number, month: number, sales: Decimal }[],
  totalDevSalesByMonth: { year: number, month: number, sales: Decimal }[],
): { year: number, month: number, sales: Decimal }[] => {
  const functionName = "calcProductSalesByMonth";
  try {
    // 各配列を結合
    const combinedSales = [
      ...totalSalesByPartNoMonth,
      ...totalMoldSalesByMonth,
      ...totalDevSalesByMonth,
    ];

    // 集約処理
    const aggregatedSales = combinedSales.reduce<{
      [key: string]: { year: number; month: number; sales: Decimal };
    }>((acc, { year, month, sales }) => {
      // 年と月を識別するキーを作成
      const key = `${year}-${month}`;

      if (!acc[key]) {
        // 初期化
        acc[key] = { year, month, sales: new Decimal(0) };
      }

      // 各売上を合算
      acc[key].sales = acc[key].sales.plus(sales);

      return acc;
    }, {});
    // オブジェクトを配列に変換
    return Object.values(aggregatedSales);

  } catch (error) {
    console.error(`${functionName} ERROR: `, error);
    return [];
  }
};

/**
 * 製品売上の算出（年別）
 *
 * @param {{ year: number, month: number, sales: Decimal }[]} productSalesByMonth 製品売上
 * @returns {Decimal[]} 製品売上
 */
export const calcProductSalesByYear = (
  productSalesByMonth: { year: number, month: number, sales: Decimal }[],
): Decimal[] => {
  const functionName = "calcProductSalesByYear";
  try {
    // 合算処理
    const salesByYear = productSalesByMonth.reduce<{
      [year: number]: Decimal;
    }>((acc, { year, sales }) => {
      // 該当の年がまだ存在しなければ初期化
      if (!acc[year]) {
        acc[year] = new Decimal(0);
      }
      // 売上を該当年に加算
      acc[year] = acc[year].plus(sales);
      return acc;
    }, {});
    console.debug(Object.values(salesByYear));
    return Object.values(salesByYear)

  } catch (error) {
    console.error(`${functionName} ERROR: `, error);
    return [];
  }
};

/**
 * Decimal型配列の合計を算出
 * @param arrays Decimal配列
 * @returns 
 */
export const sumDecimalArray = (...arrays: Decimal[][]): Decimal[] => {
  const functionName = "sumDecimalArray";
  try {
    const maxLength = Math.max(...arrays.map((arr) => arr.length));
    // すべての配列を同じインデックスごとに加算
    return Array.from({ length: maxLength }, (_, index) =>
      arrays.reduce(
        (sum, array) => sum.plus(array[index] || new Decimal(0)),
        new Decimal(0) // 初期値は Decimal(0)
      )
    );
  } catch (error) {
    console.error(`${functionName} ERROR: `, error);
    return [];
  }
};

/**
 * Decimal型配列の引値を算出
 * @param totalArray 
 * @param subtractedArrays 
 * @returns 
 */
export const subtractDecimalArray = (
  totalArray: Decimal[],
  ...subtractedArrays: Decimal[][]
): Decimal[] => {
  const functionName = "subtractDecimalArray";
  try {
    const maxLength = Math.max(
      totalArray.length,
      ...subtractedArrays.map((arr) => arr.length)
    ); // 最長配列の長さを取得

    // totalArrayから他のすべての配列を同じインデックスごとに減算
    return Array.from({ length: maxLength }, (_, index) => {
      const totalValue = totalArray[index] || new Decimal(0);
      const subtraction = subtractedArrays.reduce(
        (subSum, array) => subSum.plus(array[index] || new Decimal(0)),
        new Decimal(0) // 初期値は Decimal(0)
      );
      return totalValue.minus(subtraction); // 差を計算
    });
  } catch (error) {
    console.error(`${functionName} ERROR: `, error);
    return [];
  }
};

/**
 * 回収期間の算出
 * @param recoveredResourcesByMonth 
 * @param capexAmountByMonth 
 * @param hasDecimal 
 * @param startDate 
 * @returns 
 */
export const calcMonthlyRecovery = (
  recoveredResourcesByMonth: Decimal[], // 回収資源（正の数）
  capexAmountByMonth: { equipmentId: string; year: number; month: number; capexAmount: Decimal }[][], // 設備投資金額（負の数）
  hasDecimal: boolean,
  startDate: string,
): string => {
  const functionName = "calcMonthlyRecovery";

  try {
    const monthlyRecovery: Decimal[] = [];
    let currentRecovery = new Decimal(0);

    for (let i = 0; i < recoveredResourcesByMonth.length; i++) {
      // 累積回収額に回収資源を加算（正の値）
      const monthRecovery = recoveredResourcesByMonth[i];
      currentRecovery = currentRecovery.plus(monthRecovery);

      // 各設備の設備投資金額を累積回収額に加算（負の値）
      capexAmountByMonth.forEach((equipList) => {
        const equipmentData = equipList[i];
        if (equipmentData && equipmentData.capexAmount) {
          const capexAmount = equipmentData.capexAmount.negated().toDecimalPlaces(hasDecimal ? 3 : 0);
          currentRecovery = currentRecovery.plus(capexAmount);
        }
      });

      // 各月の累積回収額を保存
      monthlyRecovery.push(currentRecovery);
    }

    // 回収が正の値になる最初の月を検出
    const nonNegativeIndexes = monthlyRecovery
      .map((val, idx) => (val?.toNumber() >= 0 && (idx === 0 || monthlyRecovery[idx - 1]?.toNumber() < 0) ? idx : null))
      .filter((val) => val !== null);

    if (nonNegativeIndexes.length === 0) {
      return i18n.t('common.label.irrecoverable', { defaultValue: '回収不能' });
    }

    let recoveryPeriod = "";
    nonNegativeIndexes.forEach((month, i) => {
      // formatRecoveryMonthsと同じ計算方法を使用
      // month は 0-based index なので、そのまま使用
      const index = month as number;
      const recoveryYear = Math.floor(index / 12);
      const recoveryMonth = index % 12;

      recoveryPeriod += `${i > 0 ? ", " : ""}${i18n.t('common.dateFormat.yearMonthPeriod', { year: recoveryYear, month: recoveryMonth, defaultValue: `${recoveryYear}年${recoveryMonth}ヶ月` })}`;
    });

    return recoveryPeriod;

  } catch (error) {
    console.error(`${functionName} ERROR:`, error);
    return "";
  }
};

/**
 * 回収期間の算出
 * @param capexAmountByYear 設備投資金額
 * @param profitByYear 営業利益
 * @param interestRateByYear 利益
 * @param depreciationByYear 減価償却費
 * @param productLife 製品寿命
 * @returns 回収期間
 */
export const calcPaybackPeriod = (capexAmountByYear: Decimal[], profitByYear: Decimal[],
  interestRateByYear: Decimal[], depreciationByYear: Decimal[], productLife: number) => {

  const functionName = "calcPaybackPeriod";

  try {
    // 合計算出
    const calcTotal = (acc: Decimal, value: Decimal) => acc.plus(value);
    const totalCapexAmount = capexAmountByYear.reduce(calcTotal, new Decimal(0));
    const totalProfit = profitByYear.reduce(calcTotal, new Decimal(0));
    const totalInterestRate = interestRateByYear.reduce(calcTotal, new Decimal(0));
    const totalDepreciation = depreciationByYear.reduce(calcTotal, new Decimal(0));

    // 設備投資額 ÷ ( (営業利益の合計 - 金利の合計) × 50% ＋ (減価償却費) ) × 製品寿命
    const denominator = totalProfit.minus(totalInterestRate).mul(0.5).plus(totalDepreciation);

    // 分母が0（Infinity/NaNの原因）の場合は明示的に回収不能扱いにする
    if (denominator.isZero()) {
      return i18n.t('common.label.irrecoverable', { defaultValue: '回収不能' });
    }

    const paybackPeriod = totalCapexAmount.div(denominator).mul(productLife);

    if (paybackPeriod.isNegative()) {
      return i18n.t('common.label.irrecoverable', { defaultValue: '回収不能' });
    }

    // Ｍ年ｎヶ月変換
    const year = paybackPeriod.floor();
    const month = paybackPeriod.minus(year).mul(12);

    return i18n.t('common.dateFormat.yearMonthPeriod', { year, month: month.floor(), defaultValue: `${year}年${month.floor()}ヶ月` });
  } catch (error) {
    console.error(`${functionName} ERROR:`, error);
    return ``;
  }
}

/**
 * 累計設備投資額の算出
 * @param capexAmountByYear
 * @returns 
 */
export const calcTotalCapexAmount = (
  capexAmountByYear: Decimal[],
): Decimal[] => {
  const functionName = "calcTotalCapexAmount";
  try {
    const totalCapexAmountByYear: Decimal[] = (() => {
      const total = capexAmountByYear.reduce((acc, cur) => acc.plus(cur), new Decimal(0));
      return [total, ...Array(capexAmountByYear.length - 1).fill(new Decimal(0))];
    })();
    return totalCapexAmountByYear;
  } catch (error) {
    console.error(`${functionName} ERROR:`, error);
    return [];
  }
};

/**
 * 年月ラベルの作成
 * @param projectStartDate 
 * @param maxNumberOfYear 
 * @returns 
 */
export const calcLabelsByMonth = (
  projectStartDate: string | null | undefined,
  maxNumberOfYear: number,
): string[] => {
  const functionName = "calcLabelsByMonth";
  try {
    if (!projectStartDate) {
      return [];
    }
    const startDateObj = dayjs(projectStartDate);
    const startMonth = startDateObj.month() + 1;
    // 初年度の月数を計算（startMonthから3月までの月数）
    const firstFiscalMonths = startMonth < 4 ? 4 - startMonth : 12 - (startMonth - 4);
    // 年度ごとの月数リストを作成（初年度: firstFiscalMonths, それ以降: 12ヶ月）
    const monthsPerYear = [firstFiscalMonths, ...Array(maxNumberOfYear - 1).fill(12)];
    // 総月数を計算
    const totalMonths = monthsPerYear.reduce((sum, m) => sum + m, 0);
    const monthlyLabels: string[] = [];
    let current = dayjs(projectStartDate);
    for (let m = 0; m < totalMonths; m++) {
      monthlyLabels.push(current.format("YYYY/MM"));
      current = current.add(1, "month");
    }
    return monthlyLabels;
  } catch (error) {
    console.error(`${functionName} ERROR:`, error);
    return [];
  }
};

// #region 2025/10/06 追加分

/**
 * 設備投資額の集計
 * @param equipmentAmountsByMonth 設備投資額（設備ごとの設備投資額）
 * @param useElapsedYears フラグ: 年度ではなく経過年数を使用する場合は true
 * @param projectStartDate プロジェクト開始日 (経過年数計算時に使用)
 * @returns 設備投資額（全ての設備の集計結果）
 */
export const calcAggregateCapexByMonth = (
  equipmentAmountsByMonth: {
    equipmentId: string;
    year: number;
    month: number;
    capexAmount: Decimal;
  }[][],
  useElapsedYears: boolean = false,
  projectStartDate?: string
): {
  year: number;
  month: number;
  capexAmount: Decimal;
}[] => {

  const functionName = "calcAggregateCapexByMonth";
  try {

    const result: { [key: string]: Decimal } = {};

    // データを年月で集計
    equipmentAmountsByMonth.forEach(equipmentList => {
      equipmentList.forEach(entry => {
        const key = `${entry.year}-${entry.month}`;
        const amount = new Decimal(entry.capexAmount || "0");
        if (result[key]) {
          result[key] = result[key].add(amount);
        } else {
          result[key] = amount;
        }
      });
    });

    // プロジェクト開始日の設定（経過年数用）
    const projectStartDateObj = projectStartDate ? new Date(projectStartDate) : undefined;

    // オブジェクトから配列に変換
    const aggregatedData: {
      year: number;
      month: number;
      capexAmount: Decimal;
    }[] = Object.entries(result).map(([key, capexAmount]) => {
      const [originalYear, month] = key.split("-").map(Number);

      // 年度または経過年数の切り替え
      let calculatedYear = originalYear; // デフォルトは元の年
      if (useElapsedYears && projectStartDateObj) {
        const startYear = projectStartDateObj.getFullYear();
        const startMonth = projectStartDateObj.getMonth() + 1; // 月は0インデックス
        if (month >= startMonth) {
          calculatedYear = originalYear - startYear + 1; // 経過年数を算出
        } else {
          calculatedYear = originalYear - startYear; // 昨年として扱う
        }
      }

      return {
        year: calculatedYear,
        month,
        capexAmount,
      };
    });

    // 年月順で並び替え
    aggregatedData.sort((a, b) => (a.year - b.year) || (a.month - b.month));

    // 計算結果を返却
    return aggregatedData;

  } catch (error) {
    console.error(`${functionName} ERROR: `, error);
    return [];
  }
};

/**
 * 複数の年月ごとの value を基本リストに加算
 * @param baseValues 基本となる年月リスト（{ year, month, value }の配列）
 * @param additionalValueLists 可変長引数で渡される複数の年月リスト（{ year, month, value }の配列）
 * @returns 加算後の年月リスト（被る年月は加算して返却）
 */
export const mergeYearMonthValues = (
  baseValues: { year: number; month: number; value: Decimal }[],
  ...additionalValueLists: { year: number; month: number; value: Decimal }[][]
): { year: number; month: number; value: Decimal }[] => {
  const functionName = "mergeYearMonthValues";

  try {
    // 統一された Map 作成：すべての年月データを累積的に処理
    const valueMap = new Map<string, Decimal>();

    // **1. baseValues の処理**（同じ年月を統合）
    baseValues.forEach(({ year, month, value }) => {
      const key = `${year}-${month}`;
      if (!valueMap.has(key)) {
        valueMap.set(key, new Decimal(value)); // 初回追加
      } else {
        valueMap.set(key, valueMap.get(key)!.plus(value)); // 加算処理
      }
    });

    // **2. additionalValueLists の処理**（同じ年月を統合）
    additionalValueLists.forEach((list) => {
      list.forEach(({ year, month, value }) => {
        const key = `${year}-${month}`;
        if (!valueMap.has(key)) {
          valueMap.set(key, new Decimal(value)); // 初回追加
        } else {
          valueMap.set(key, valueMap.get(key)!.plus(value)); // 加算処理
        }
      });
    });

    // **3. 結果リストを作成**：Map を使用して統一されたリストを生成
    const result = Array.from(valueMap.entries()).map(([key, value]) => {
      const [yearString, monthString] = key.split("-");
      return {
        year: Number(yearString),
        month: Number(monthString),
        value,
      };
    });

    return result;
  } catch (error) {
    console.error(`${functionName} ERROR: `, error);
    return [];
  }
};

/**
 * 複数の年月ごとの value を基本リストから減算し、全リストの最大要素数に基づいて返却
 *
 * @param baseValues 基本となる年月リスト（{ year, month, value }の配列）
 * @param subtractValueLists 可変長引数で渡される複数の年月リスト（{ year, month, value }の配列）
 * @returns 減算後の年月リスト（すべてのリストの最大要素数に基づく）
 */
export const subtractYearMonthValues = (
  baseValues: { year: number; month: number; value: Decimal }[],
  ...subtractValueLists: { year: number; month: number; value: Decimal }[][]
): { year: number; month: number; value: Decimal }[] => {
  // すべての年月キーを収集
  const allKeys = new Set<string>();

  // 基本リストの年月キーを追加
  baseValues.forEach(({ year, month }) => {
    allKeys.add(`${year}-${month}`);
  });

  // subtractValueLists の年月キーを追加
  subtractValueLists.forEach((list) => {
    list.forEach(({ year, month }) => {
      allKeys.add(`${year}-${month}`);
    });
  });

  // 減算対象の値を統合し、Map を作成
  const subtractMap = new Map<string, Decimal>();
  subtractValueLists.forEach((list) => {
    list.forEach(({ year, month, value }) => {
      const key = `${year}-${month}`;
      if (!subtractMap.has(key)) {
        // キーが初めての場合、新規に設定
        subtractMap.set(key, value);
      } else {
        // 既存キーがある場合、値を累積で統合
        subtractMap.set(key, subtractMap.get(key)!.plus(value));
      }
    });
  });

  // すべての年月キーを基準に減算
  const result: { year: number; month: number; value: Decimal }[] = Array.from(allKeys).map((key) => {
    const [yearString, monthString] = key.split('-'); // キーを分割して年月を取得
    const year = Number(yearString);
    const month = Number(monthString);

    // 基本値を取得（該当しない場合は 0）
    const baseValue = baseValues.find((item) => item.year === year && item.month === month)?.value || new Decimal(0);

    // 減算対象値を取得（該当しない場合は 0）
    const subtractValue = subtractMap.get(key) || new Decimal(0);

    return {
      year,
      month,
      value: baseValue.minus(subtractValue), // 減算結果
    };
  });

  return result;
};

/**
 * NOPATの算出 (月別)
 *
 * @param profitByMonth 月別の EBIT （{ year, month, value } の配列）
 * @param taxRate 実効税率
 * @param hasDecimal 小数有無
 * @returns 月別の NOPAT （{ year, month, value } の配列）
 */
export const calcNopatByMonth = (
  profitByMonth: { year: number; month: number; value: Decimal }[],
  taxRate: Decimal,
  hasDecimal: boolean,
): { year: number; month: number; value: Decimal }[] => {
  const functionName = "calcNopatByMonth";
  try {
    // 税引後率を計算
    const afterTaxRate = new Decimal(100).minus(taxRate).div(100);

    // NOPATを取得
    return profitByMonth.map(({ year, month, value }) => {
      // NOPAT = EBIT * 税引後率
      const nopatValue = value.times(afterTaxRate);
      const roundedNopatValue = new Decimal(nopatValue.toFixed(hasDecimal ? 3 : 0)); // 小数点以下を処理
      return {
        year,
        month,
        value: roundedNopatValue,
      };
    });
  } catch (error) {
    console.error(`${functionName} ERROR: `, error);
    return [];
  }
};






/**
* 減価償却費の算出(設備別に償却開始月から計上)
*
* @param {number} maxYears 表示年数
* @param {Decimal} convertedCapexEquipment 設備投資額
* @param {boolean} hasDecimal 小数有無
* @param {string} projectStartDate プロジェクト開始日
* @param {boolean} zeroFiscalFlg 0年度開始フラグ
* @returns {Array<{ year: number; month: number; value: Decimal }>} 月ごとの減価償却費配列
*/
export const calcEquipmentDepreciationByYearMonth = (
  maxYears: number,
  convertedCapexEquipment: EquipmentType[],
  hasDecimal: boolean,
  projectStartDate: string,
  zeroFiscalFlg: boolean,
): { year: number; month: number; value: Decimal }[] => {
  const functionName = "calcEquipmentDepreciationByYearMonth";

  try {
    const startDateObj = dayjs(projectStartDate);
    const startMonth = startDateObj.month() + 1;
    const totalFiscalYears = zeroFiscalFlg ? maxYears + 1 : maxYears;

    // 開始日と0年度の計算
    let beforeStartDate = zeroFiscalFlg
      ? dayjs().set('year', startMonth < 4 ? startDateObj.year() - 2 : startDateObj.year() - 1).set('month', 3).set('date', 1)
      : startDateObj;

    // 総月数の計算
    const totalMonths = totalFiscalYears * 12;

    // 月ごとの減価償却費を格納するMap（キー: "year-month"）
    const monthlyDepreciationMap: Map<string, { year: number; month: number; value: Decimal }> = new Map();

    // 初期化：全ての月を0で初期化
    let initCurrent = beforeStartDate;
    let initFiscalYear = zeroFiscalFlg ? 0 : 1;
    for (let m = 0; m < totalMonths; m++) {
      // 年度を 4月開始でカウントするためのロジック
      if (initCurrent.month() + 1 === 4 && m > 0) {
        initFiscalYear++;
      }
      const key = `${initFiscalYear}-${initCurrent.month() + 1}`;
      monthlyDepreciationMap.set(key, {
        year: initFiscalYear,
        month: initCurrent.month() + 1,
        value: new Decimal(0),
      });
      initCurrent = initCurrent.add(1, "month");
    }

    convertedCapexEquipment.forEach((equip) => {
      // 各設備の月々の償却費を計算
      const decAmount = new Decimal(equip.amount || 0);
      const decDepreciationPeriod = new Decimal(equip.depreciationPeriod || 0);
      const decDepreciationMonth = decDepreciationPeriod.mul(12);
      // 償却年数が0/未設定（decDepreciationMonthが0以下）の場合はゼロ除算となり
      // Infinity/NaNがP&L全体へ伝播するため、その場合は償却費0として扱う（FE-BUG-01対応）
      const isInvalidDepreciationMonth = decDepreciationMonth.isZero() || decDepreciationMonth.isNegative();
      const depreciationByMonth = isInvalidDepreciationMonth
        ? new Decimal(0)
        : decAmount.div(decDepreciationMonth);

      const dayDepreciationStartMonth = dayjs(equip.depreciationStartMonth);
      // decDepreciationMonthが0以下の場合、minus(1)で負値となり日付計算が不正になるため、
      // 償却期間なしとして開始月自身を終了月とする（対象月なしの状態にする）
      const decDepreciationEndMonth = isInvalidDepreciationMonth
        ? dayDepreciationStartMonth.subtract(1, "month")
        : dayDepreciationStartMonth.add(Number(decDepreciationMonth.minus(1)), "month");

      let current = beforeStartDate;
      let fiscalYear = zeroFiscalFlg ? 0 : 1;

      for (let m = 0; m < totalMonths; m++) {
        // 年度を 4月開始でカウントするためのロジック
        if (current.month() + 1 === 4 && m > 0) {
          fiscalYear++;
        }

        let tmpDepreciationByMonth = new Decimal(0);

        // 償却開始月～終了月間で償却費を加算
        if (
          current.isAfter(dayDepreciationStartMonth.subtract(1, "month"), 'month') &&
          (current.isBefore(decDepreciationEndMonth, 'month') || current.isSame(decDepreciationEndMonth, 'month'))
        ) {
          tmpDepreciationByMonth = depreciationByMonth;
        }

        // 月ごとに合計（複数設備対応）
        const key = `${fiscalYear}-${current.month() + 1}`;
        const existing = monthlyDepreciationMap.get(key);
        if (existing) {
          existing.value = existing.value.add(tmpDepreciationByMonth);
        }

        current = current.add(1, "month");
      }
    });

    // Mapを配列に変換
    const monthlyDepreciation = Array.from(monthlyDepreciationMap.values());

    // 割り算による小数点誤差を避けるため 11 桁で保持（通貨精度への丸めは下流で実施）
    const formattedMonthlyDepreciation = monthlyDepreciation.map(({ year, month, value }) => ({
      year,
      month,
      value: value.toDecimalPlaces(11),
    }));

    // データの切り捨て処理 (maxYears分のみ返却)
    const finalMonthlyDepreciation = formattedMonthlyDepreciation.filter(({ year }) => year <= maxYears);

    // 月々の減価償却費配列を返却
    return finalMonthlyDepreciation;

  } catch (error) {
    console.error(`${functionName} ERROR: `, error);
    return [];
  }
};

// #endregion

// #region 日付計算ユーティリティ

/**
 * 2つの日付間の月数を計算する
 * 同月の場合は1ヶ月、翌月の場合は2ヶ月として計算
 * @param startDate 開始日 (YYYY-MM-DD形式)
 * @param endDate 終了日 (YYYY-MM-DD形式)
 * @returns 月数（同月=1、翌月=2、...）
 */
export const calcMonthsBetweenDates = (
  startDate: string | null | undefined,
  endDate: string | null | undefined
): number => {
  const functionName = "calcMonthsBetweenDates";
  try {
    if (!startDate || !endDate) {
      return 0;
    }
    const start = dayjs(startDate);
    const end = dayjs(endDate);

    if (!start.isValid() || !end.isValid()) {
      console.warn(`${functionName}: Invalid date format. startDate=${startDate}, endDate=${endDate}`);
      return 0;
    }

    // 終了日が開始日より前の場合は0を返す
    if (end.isBefore(start)) {
      console.warn(`${functionName}: endDate is before startDate. startDate=${startDate}, endDate=${endDate}`);
      return 0;
    }

    // 月の差分を計算（同月=0となるため+1して同月=1とする）
    const months = end.diff(start, 'month') + 1;
    return months;
  } catch (error) {
    console.error(`${functionName} ERROR:`, error);
    return 0;
  }
};

// #endregion

// ここから新しい計算処理を記載します。いまごちゃごちゃしているので、一度全体を整理したい…

/**
 * 月別のサイクルタイム合計を算出
 *
 * 各工程のサイクルタイム（月別）を合計して、月ごとの全PartNo合計のサイクルタイムを返却します。
 * 浮動小数点の精度問題を避けるため、Decimal.jsを使用して計算します。
 *
 * @param {UniqueProcessType[]} uniqueProcessData 工程データ配列
 * @param {{ year: number; month: number }[]} yearMonthListByFiscalYear 年月リスト（年度ベース）
 * @returns {Decimal[]} 月別のサイクルタイム合計（秒）
 */
export const calcTotalCycleTimeByMonth = (
  uniqueProcessData: UniqueProcessType[],
  yearMonthListByFiscalYear: { year: number; month: number }[]
): Decimal[] => {
  const functionName = "calcTotalCycleTimeByMonth";
  try {
    // 月ごとに全工程・全PartNoのサイクルタイム合計を計算
    const result = yearMonthListByFiscalYear.map(({ year, month }) => {
      // "YYYY-MM"形式のキーを生成
      const yearMonthKey = `${year}-${String(month).padStart(2, '0')}`;
      let totalCycleTime = new Decimal(0);

      for (const process of uniqueProcessData) {
        const cycleTimeList = process.uniqueProcessCycleTimeInSecondByPartNoList;
        if (cycleTimeList && cycleTimeList.length > 0) {
          for (const entry of cycleTimeList) {
            // 月別サイクルタイムから該当月の値を取得
            const cycleTimeValue = entry.cycleTimeMonthly?.[yearMonthKey] || "0";
            totalCycleTime = totalCycleTime.plus(new Decimal(cycleTimeValue));
          }
        }
      }
      return totalCycleTime;
    });

    // console.log(`${functionName} RESULT (length: ${result.length})`);
    return result;

  } catch (error) {
    console.error(`${functionName} ERROR:`, error);
    return [];
  }
};

/**
 * 全PartNoの月別数量合計を算出
 *
 * PartNoごと・年度ごとの数量データをフラット化し、月別に全PartNoの数量を合計します。
 *
 * @param {Array} quantitiesByPartNoMonth PartNoごと・年度ごとの月別数量データ
 * @param {number} totalMonths 合計月数
 * @returns {Decimal[]} 月別の全PartNo数量合計
 */
export const calcTotalQuantityByMonth = (
  quantitiesByPartNoMonth: { partNo: string, year: number, quantities: Decimal[] }[],
  totalMonths: number
): Decimal[] => {
  const functionName = "calcTotalQuantityByMonth";
  try {
    // 全PartNoの月別数量をフラット化
    const allPartNoQuantitiesFlat: { partNo: string, quantities: Decimal[] }[] = [];
    const partNoSet = new Set(quantitiesByPartNoMonth.map(q => q.partNo));
    partNoSet.forEach(partNo => {
      const partNoYearData = quantitiesByPartNoMonth
        .filter(q => q.partNo === partNo)
        .sort((a, b) => a.year - b.year);
      const flatQuantities = partNoYearData.flatMap(q => q.quantities);
      allPartNoQuantitiesFlat.push({ partNo, quantities: flatQuantities });
    });

    // 月別に全PartNoの数量を合計
    const totalQuantityByMonth: Decimal[] = [];
    for (let i = 0; i < totalMonths; i++) {
      let monthTotal = new Decimal(0);
      allPartNoQuantitiesFlat.forEach(partNoData => {
        if (i < partNoData.quantities.length) {
          monthTotal = monthTotal.plus(partNoData.quantities[i] || new Decimal(0));
        }
      });
      totalQuantityByMonth.push(monthTotal);
    }

    // console.log(`${functionName} RESULT (length: ${totalQuantityByMonth.length})`);
    return totalQuantityByMonth;

  } catch (error) {
    console.error(`${functionName} ERROR:`, error);
    return [];
  }
};

/**
 * サイクルタイムベースのコスト計算（汎用関数）
 *
 * 計算式: ライン全体のC/T合計（分）× 単価（通貨/分）× 販売個数（月別）
 *
 * 生産工場労務費、償却費以外の工場経費など、
 * サイクルタイムと単価に基づくコスト計算に使用します。
 *
 * @param {Decimal[]} cycleTimeInSecondsByMonth 月別のライン全体のC/T合計（秒）
 * @param {Decimal} costPerMinute 単価（通貨/分）
 * @param {string | undefined} sourceCurrencyName 単価の通貨名（変換元）
 * @param {string | undefined} targetCurrencyName 変換先通貨名（記帳通貨）
 * @param {Currency[] | undefined} currencyData 通貨データ配列
 * @param {Decimal[]} salesQuantityByMonth 月別販売数量合計（全PartNo合計）
 * @param {boolean} hasDecimal 小数有無
 * @param {"yearly" | "monthly"} scale 返却スケール（年間または月間）
 * @param {string} functionName ログ出力用の関数名（オプション）
 * @returns {Decimal[]} コスト(年度別または月別)
 */
export const calcCycleTimeBasedCost = (
  cycleTimeInSecondsByMonth: Decimal[],
  costPerMinute: Decimal,
  sourceCurrencyName: string | undefined,
  targetCurrencyName: string | undefined,
  currencyData: Currency[] | undefined,
  salesQuantityByMonth: Decimal[],
  hasDecimal: boolean,
  scale: "yearly" | "monthly",
  functionName: string = "calcCycleTimeBasedCost",
): Decimal[] => {
  try {
    // 引数コンソール出力
    /* console.log(`${functionName} ARGUMENTS. ${JSON.stringify({
      cycleTimeInSecondsByMonthLength: cycleTimeInSecondsByMonth?.length,
      costPerMinute: costPerMinute,
      sourceCurrencyName: sourceCurrencyName,
      targetCurrencyName: targetCurrencyName,
      salesQuantityByMonthLength: salesQuantityByMonth?.length,
      hasDecimal: hasDecimal,
      scale: scale,
    })}`); */

    // 単価を通貨変換（変換元・変換先が設定されている場合のみ）
    const convertedCostPerMinute = sourceCurrencyName && targetCurrencyName && currencyData
      ? new Decimal(convertCurrency(costPerMinute, sourceCurrencyName, targetCurrencyName, currencyData))
      : costPerMinute;

    // 月別コストを計算
    const costMonthly: Decimal[] = salesQuantityByMonth.map((quantity, index) => {
      // 該当月のサイクルタイムを取得（秒→分に変換）
      const cycleTimeInMinutes = (cycleTimeInSecondsByMonth[index] ?? new Decimal(0)).div(60);
      // コスト = サイクルタイム（分）× 単価（通貨/分）× 販売数量
      const cost = cycleTimeInMinutes.times(convertedCostPerMinute).times(quantity);
      return new Decimal(cost.toDecimalPlaces(hasDecimal ? 3 : 0, Decimal.ROUND_HALF_UP));
    });

    if (scale === "monthly") {
      // 月間を返却
      // console.log(`${functionName} RESULT ${JSON.stringify(costMonthly)}`);
      return costMonthly;
    } else if (scale === "yearly") {
      // 月間コスト ⇒ 年間コスト 変換
      const costYearly = convertMonthlyToYearly(costMonthly, hasDecimal);

      // 年間を返却
      // console.log(`${functionName} RESULT ${JSON.stringify(costYearly)}`);
      return costYearly;
    }

    return [];
  } catch (error) {
    console.error(`${functionName} ERROR: `, error);
    return [];
  }
}

/**
 * 月別データを年度別データに変換する汎用関数
 *
 * @param {Decimal[]} monthlyData 月別データ配列
 * @param {boolean} hasDecimal 小数有無（シグネチャ互換のため残置、丸めなし化により未使用）
 * @param {string} functionName ログ出力用の関数名（オプション）
 * @returns {Decimal[]} 年度別データ配列（丸めなし、フル精度）
 */
export const convertMonthlyToYearly = (
  monthlyData: Decimal[],
  hasDecimal: boolean,
  functionName: string = "convertMonthlyToYearly",
): Decimal[] => {
  try {
    // 引数コンソール出力
    /* console.log(`${functionName} ARGUMENTS. ${JSON.stringify({
      monthlyDataLength: monthlyData?.length,
      hasDecimal: hasDecimal,
    })}`); */

    // 入力データがない場合は空配列を返却
    if (!monthlyData || monthlyData.length === 0) {
      // console.log(`${functionName} RESULT []`);
      return [];
    }

    // 返却用変数宣言
    const yearlyData: Decimal[] = [];

    let firstYearEndIndex = 0;

    // 開始日が4月以外の場合、余りが初年度の終わり
    firstYearEndIndex = monthlyData.length % 12;

    // 開始日が4月の場合、余りが0なので、初年度は12月分あるので、12にする。
    firstYearEndIndex = firstYearEndIndex === 0 ? 12 : firstYearEndIndex;

    // 集計用変数
    let totalYear = new Decimal(0);

    monthlyData.forEach((value, idx) => {
      // 年度毎に加算
      totalYear = totalYear.plus(value);

      // 年度の終わりに格納
      if (idx === (firstYearEndIndex - 1) || (idx - (firstYearEndIndex - 1)) % 12 === 0) {
        // 年度毎集計結果を格納
        yearlyData.push(totalYear);

        // 初期化
        totalYear = new Decimal(0);
      }
    });

    // console.log(`${functionName} RESULT ${JSON.stringify(yearlyData)}`);
    return yearlyData;
  } catch (error) {
    console.error(`${functionName} ERROR: `, error);
    return [];
  }
}

/**
 * 月別値データ（MonthValue[]）を通貨変換してプロジェクト月に合わせたDecimal配列に変換
 * - プロジェクトの年月リストと照合
 * - 期間内のデータ → 通貨変換して採用
 * - 期間外のデータ → 無視
 * - 該当月データなし → 0を設定
 *
 * @param {MonthValue[]} monthlyInput 月別入力データ
 * @param {string | undefined} sourceCurrencyName 変換元通貨名
 * @param {string | undefined} targetCurrencyName 変換先通貨名
 * @param {Currency[] | undefined} currencyData 通貨マスタデータ
 * @param {{ year: number, month: number }[]} yearMonthList プロジェクトの年月リスト
 * @param {boolean} hasDecimal 小数有無（シグネチャ互換のため残置、丸めなし化により未使用）
 * @param {string} functionName ログ出力用の関数名（オプション）
 * @returns {Decimal[]} プロジェクト月数分のDecimal配列（丸めなし、フル精度）
 */
export const convertMonthlyValueData = (
  monthlyInput: MonthValue[],
  sourceCurrencyName: string | undefined,
  targetCurrencyName: string | undefined,
  currencyData: Currency[] | undefined,
  yearMonthList: { year: number, month: number }[],
  hasDecimal: boolean,
  functionName: string = "convertMonthlyValueData",
  convertFn?: ConvertFn
): Decimal[] => {
  try {
    const convert = convertFn
      || ((amount: number | Decimal, from: string, to: string) => convertCurrency(amount, from, to, currencyData!));
    // 引数コンソール出力
    /* console.log(`${functionName} ARGUMENTS. ${JSON.stringify({
      monthlyInputLength: monthlyInput?.length,
      sourceCurrencyName: sourceCurrencyName,
      targetCurrencyName: targetCurrencyName,
      yearMonthListLength: yearMonthList?.length,
      hasDecimal: hasDecimal,
    })}`); */

    // 入力データがない場合は空配列を返却
    if (!monthlyInput || monthlyInput.length === 0 || !yearMonthList || yearMonthList.length === 0) {
      // console.log(`${functionName} RESULT []`);
      return [];
    }

    // 入力データをMapに変換（高速検索用）
    const inputMap = new Map<string, number>();
    monthlyInput.forEach(item => {
      const key = `${item.year}-${item.month}`;
      inputMap.set(key, item.value);
    });

    // プロジェクトの年月リストに合わせて値を取得・変換
    const result: Decimal[] = yearMonthList.map(({ year, month }) => {
      const key = `${year}-${month}`;
      const value = inputMap.get(key) ?? 0;
      const decimalValue = new Decimal(value);

      // 通貨変換（変換元・変換先が設定されている場合のみ）
      const yearMonth = `${year}-${String(month).padStart(2, "0")}`;
      const convertedValue = sourceCurrencyName && targetCurrencyName && (convertFn || currencyData)
        ? new Decimal(convert(decimalValue, sourceCurrencyName, targetCurrencyName, yearMonth))
        : decimalValue;

      return convertedValue;
    });

    // console.log(`${functionName} RESULT ${JSON.stringify(result)}`);
    return result;
  } catch (error) {
    console.error(`${functionName} ERROR: `, error);
    return [];
  }
}

/**
 * DB保存直前に、ネストされたオブジェクト・配列内の数値文字列を指定桁数に丸める
 * - Decimal.js（precision=50）で計算した結果をそのまま保存すると桁数が50桁前後まで肥大化するため、
 *   保存直前の1回だけ丸めて桁数を抑える
 * - 計算過程（通貨変換の途中）で丸めるのではなく、全ての計算が終わった後の最終値にのみ適用することで、
 *   往復通貨変換による丸め誤差の再発を避ける（No425調査で判明した問題と同種の再発防止）
 * - 通貨コード・日付・年月ラベル等、数値としてパースできない文字列はそのまま返す
 *
 * @param {any} data 丸め対象のデータ（オブジェクト・配列・文字列等、再帰的に処理）
 * @param {number} decimalPlaces 丸める小数点以下の桁数（デフォルト5）
 * @returns {any} 数値文字列のみ丸めた後のデータ（元のデータ構造は変更しない）
 */
export const roundNumericStringsDeep = (data: any, decimalPlaces: number = 5): any => {
  if (Array.isArray(data)) {
    return data.map(item => roundNumericStringsDeep(item, decimalPlaces));
  }
  if (data !== null && typeof data === 'object') {
    const result: any = {};
    for (const key of Object.keys(data)) {
      result[key] = roundNumericStringsDeep(data[key], decimalPlaces);
    }
    return result;
  }
  if (typeof data === 'string' && data.trim() !== '' && !isNaN(Number(data))) {
    return new Decimal(data).toDecimalPlaces(decimalPlaces, Decimal.ROUND_HALF_UP).toString();
  }
  return data;
};

/**
 * 月別費用データ（MonthlyExpenseData[]）から特定プロパティを抽出して通貨変換
 * - プロジェクトの年月リストと照合
 * - 期間内のデータ → 通貨変換して採用
 * - 期間外のデータ → 無視
 * - 該当月データなし → 0を設定
 *
 * @param {MonthlyExpenseData[]} monthlyExpenseData 月別費用データ
 * @param {keyof Omit<MonthlyExpenseData, 'year' | 'month'>} propertyName 抽出するプロパティ名
 * @param {string | undefined} sourceCurrencyName 変換元通貨名
 * @param {string | undefined} targetCurrencyName 変換先通貨名
 * @param {Currency[] | undefined} currencyData 通貨マスタデータ
 * @param {{ year: number, month: number }[]} yearMonthList プロジェクトの年月リスト
 * @param {boolean} hasDecimal 小数有無（シグネチャ互換のため残置、丸めなし化により未使用）
 * @param {string} functionName ログ出力用の関数名（オプション）
 * @returns {Decimal[]} プロジェクト月数分のDecimal配列（丸めなし、フル精度）
 */
export const convertMonthlyExpenseData = (
  monthlyExpenseData: MonthlyExpenseData[],
  propertyName: keyof Omit<MonthlyExpenseData, 'year' | 'month'>,
  sourceCurrencyName: string | undefined,
  targetCurrencyName: string | undefined,
  currencyData: Currency[] | undefined,
  yearMonthList: { year: number, month: number }[],
  hasDecimal: boolean,
  functionName: string = "convertMonthlyExpenseData",
  convertFn?: ConvertFn
): Decimal[] => {
  try {
    const convert = convertFn
      || ((amount: number | Decimal, from: string, to: string) => convertCurrency(amount, from, to, currencyData!));
    // 引数コンソール出力
    /* console.log(`${functionName} ARGUMENTS. ${JSON.stringify({
      monthlyExpenseDataLength: monthlyExpenseData?.length,
      propertyName: propertyName,
      sourceCurrencyName: sourceCurrencyName,
      targetCurrencyName: targetCurrencyName,
      yearMonthListLength: yearMonthList?.length,
      hasDecimal: hasDecimal,
    })}`); */

    // 入力データがない場合は空配列を返却
    if (!monthlyExpenseData || monthlyExpenseData.length === 0 || !yearMonthList || yearMonthList.length === 0) {
      // console.log(`${functionName} RESULT []`);
      return [];
    }

    // 入力データをMapに変換（高速検索用）
    const inputMap = new Map<string, number>();
    monthlyExpenseData.forEach(item => {
      const key = `${item.year}-${item.month}`;
      const value = item[propertyName];
      if (value !== undefined) {
        inputMap.set(key, value);
      }
    });

    // プロジェクトの年月リストに合わせて値を取得・変換
    const result: Decimal[] = yearMonthList.map(({ year, month }) => {
      const key = `${year}-${month}`;
      const value = inputMap.get(key) ?? 0;
      const decimalValue = new Decimal(value);

      // 通貨変換（変換元・変換先が設定されている場合のみ）
      const yearMonth = `${year}-${String(month).padStart(2, "0")}`;
      const convertedValue = sourceCurrencyName && targetCurrencyName && (convertFn || currencyData)
        ? new Decimal(convert(decimalValue, sourceCurrencyName, targetCurrencyName, yearMonth))
        : decimalValue;

      return convertedValue;
    });

    // console.log(`${functionName} RESULT ${JSON.stringify(result)}`);
    return result;
  } catch (error) {
    console.error(`${functionName} ERROR: `, error);
    return [];
  }
}

/**
 * シナリオの生産工場労務費・償却費以外の工場経費でmonthlyExpenseDataを上書きしたprojectDataを生成する
 *
 * calcMonthlyProfitAndLossDetails は projectData.monthlyExpenseData から productionLaborCost /
 * overseasFactoryExpenses を読み取るため、保存済みシミュレーション設定の労務費を計算に反映する際は
 * この関数でmonthlyExpenseDataを上書きしたprojectDataを渡す。これにより通貨変換・ベースアップ率・
 * ダウンサイド調整の既存パイプラインをそのまま利用できる。
 *
 * @param projectData 元のプロジェクトデータ
 * @param scenarioLaborCostByMonth シナリオの生産工場労務費（月別、labelsに対応、未設定なら上書きしない）
 * @param scenarioOverseasFactoryExpensesByMonth シナリオの償却費以外の工場経費（月別、labelsに対応、未設定なら上書きしない）
 * @param labels 年月ラベル配列（"YYYY/M"形式）
 * @returns 上書き後のprojectData（労務費・工場経費とも未設定の場合は元のprojectDataをそのまま返す）
 */
export const applyScenarioLaborCostToProjectData = (
  projectData: ProjectWithRdsData,
  scenarioLaborCostByMonth: number[] | undefined,
  scenarioOverseasFactoryExpensesByMonth: number[] | undefined,
  labels: string[],
): ProjectWithRdsData => {
  const hasLabor = (scenarioLaborCostByMonth?.length ?? 0) > 0;
  const hasExpense = (scenarioOverseasFactoryExpensesByMonth?.length ?? 0) > 0;
  if (!hasLabor && !hasExpense) {
    // 未設定の場合は既存シナリオとの後方互換性のため、元のprojectDataをそのまま使用する
    return projectData;
  }

  const existingData: MonthlyExpenseData[] = projectData.monthlyExpenseData
    ? JSON.parse(projectData.monthlyExpenseData)
    : [];
  const dataMap = new Map<string, MonthlyExpenseData>();
  existingData.forEach(item => dataMap.set(`${item.year}-${item.month}`, item));

  labels.forEach((label, index) => {
    const [yearStr, monthStr] = label.split("/");
    const year = parseInt(yearStr, 10);
    const month = parseInt(monthStr, 10);
    if (Number.isNaN(year) || Number.isNaN(month)) {
      return;
    }
    const key = `${year}-${month}`;
    const base: MonthlyExpenseData = dataMap.get(key) ?? {
      year,
      month,
      outsourcingProcessingCost: 0,
      productionLaborCost: 0,
      domesticLaborCost: 0,
      overseasFactoryExpenses: 0,
      factoryBuildingDepreciation: 0,
      domesticFactoryExpenses: 0,
      productionSgaExpenses: 0,
      domesticSgaExpenses: 0,
      salesCompanySga: 0,
      royalty: 0,
    };
    dataMap.set(key, {
      ...base,
      productionLaborCost: hasLabor ? (scenarioLaborCostByMonth![index] ?? base.productionLaborCost) : base.productionLaborCost,
      overseasFactoryExpenses: hasExpense ? (scenarioOverseasFactoryExpensesByMonth![index] ?? base.overseasFactoryExpenses) : base.overseasFactoryExpenses,
    });
  });

  return {
    ...projectData,
    monthlyExpenseData: JSON.stringify(Array.from(dataMap.values())),
  };
};

/**
 * PartNo別月別材料単価の取得
 *
 * 各PartNoの materialCostWithInhouseProfit JSON内の monthlyData から
 * 月ごとの材料単価を合計して、月別の材料単価を返却します。
 *
 * @param {PartNo[]} capexPartNo パーツNo配列
 * @param {string | undefined} sourceCurrencyName 元通貨名
 * @param {string | undefined} targetCurrencyName 変換先通貨名
 * @param {Currency[] | undefined} currencyData 通貨データ配列
 * @param {{ year: number; month: number }[]} yearMonthList 年月リスト（年度ベース）
 * @param {"normal" | "outsourcing" | "inhouse"} type 材料単価の種別（デフォルト: "normal"）
 *   - "normal": InternalAdjustedAmount（通常の材料単価）
 *   - "outsourcing": outsourcingAmount（外注のみ）
 *   - "inhouse": inhouseAmount（内製のみ）
 * @returns {{ partNo: string, materialCostByMonth: Record<string, Decimal> }[]} PartNo別月別材料単価
 */
export const getMaterialCostByPartNoByMonth = (
  capexPartNo: PartNo[],
  sourceCurrencyName: string | undefined,
  targetCurrencyName: string | undefined,
  currencyData: Currency[] | undefined,
  yearMonthList: { year: number; month: number }[],
  type: "normal" | "outsourcing" | "inhouse" = "normal",
  convertFn?: ConvertFn
): { partNo: string, materialCostByMonth: Record<string, Decimal> }[] => {
  const functionName = "getMaterialCostByPartNoByMonth";
  try {
    const convert = convertFn
      || ((amount: number | Decimal, from: string, to: string) => convertCurrency(amount, from, to, currencyData!));
    return capexPartNo.map(part => {
      // string型チェック
      if (!part.partNo || !part.quantityAndPrice || !part.materialCostWithInhouseProfit) {
        return {
          partNo: "",
          materialCostByMonth: {},
        };
      }

      // JSON文字列をオブジェクトに変換
      const parsedQuantityAndPrice: QuantityAndPriceTable = JSON.parse(part.quantityAndPrice);
      const parsedMaterialCost: CBomDataWithMonthly[] = JSON.parse(part.materialCostWithInhouseProfit);

      // 全ての数量が0であるかを判定
      const allQuantityZero = Object.values(parsedQuantityAndPrice.data).every(item => item.quantity === 0);

      // 月別材料単価を計算
      const materialCostByMonth: Record<string, Decimal> = {};

      if (allQuantityZero) {
        // 全ての数量が0の場合、全月0
        yearMonthList.forEach(({ year, month }) => {
          const yearMonthKey = `${year}-${String(month).padStart(2, '0')}`;
          materialCostByMonth[yearMonthKey] = new Decimal(0);
        });
      } else {
        // 各月の材料単価を計算
        yearMonthList.forEach(({ year, month }) => {
          const yearMonthKey = `${year}-${String(month).padStart(2, '0')}`;

          // 全BOMアイテムの該当月の材料単価を合計
          const monthlyTotal = parsedMaterialCost.reduce((acc: Decimal, item: CBomDataWithMonthly) => {
            // monthlyDataがあれば該当月のデータを使用、なければ0
            const monthData = item.monthlyData?.[yearMonthKey];
            let costAmount: Decimal;
            if (monthData) {
              switch (type) {
                case "outsourcing":
                  costAmount = new Decimal(monthData.outsourcingAmount ?? 0);
                  break;
                case "inhouse":
                  costAmount = new Decimal(monthData.inhouseAmount ?? 0);
                  break;
                default:
                  costAmount = new Decimal(monthData.InternalAdjustedAmount ?? 0);
              }
            } else {
              costAmount = new Decimal(0);
            }
            return acc.plus(costAmount);
          }, new Decimal(0));

          // 為替変換（四捨五入なし）
          const convMaterialCost = sourceCurrencyName && targetCurrencyName && (convertFn || currencyData)
            ? convert(monthlyTotal, sourceCurrencyName, targetCurrencyName, yearMonthKey)
            : monthlyTotal;

          materialCostByMonth[yearMonthKey] = convMaterialCost;
        });
      }

      return {
        partNo: part.partNo,
        materialCostByMonth,
      };
    });
  } catch (error) {
    console.error(`${functionName} ERROR: `, error);
    return [];
  }
}

/**
 * PartNo別材料費の算出
 *
 * getMaterialCostByPartNoByMonth から取得した月別材料単価を使用して、
 * 月別・年度別の材料費を計算します。
 *
 * @param {{ partNo: string, year: number, quantities: Decimal[] }[]} quantitiesByPartNoMonth PartNo別数量(月別)
 * @param {{ partNo: string, materialCostByMonth: Record<string, Decimal> }[]} materialCostByPartNoByMonth PartNo別月別材料単価
 * @param {{ year: number; month: number }[]} yearMonthList 年月リスト（年度ベース）
 * @returns {{
 *    totalMaterialCostsByPartNoMonth: { partNo: string, year: number, totalMaterialCosts: Decimal[] }[],
 *    totalMaterialCostsByPartNoYear: { partNo: string, totalMaterialCosts: Decimal[] }[]
 * }} PartNo別材料費(月別 & 年度別)
 */
export const calcTotalMaterialCostsByPartNoWithMonthlyData = (
  quantitiesByPartNoMonth: { partNo: string, year: number, quantities: Decimal[] }[],
  materialCostByPartNoByMonth: { partNo: string, materialCostByMonth: Record<string, Decimal> }[],
  yearMonthList: { year: number; month: number }[],
  convertFn?: ConvertFn
): {
  totalMaterialCostsByPartNoMonth: { partNo: string, year: number, totalMaterialCosts: Decimal[] }[],
  totalMaterialCostsByPartNoYear: { partNo: string, totalMaterialCosts: Decimal[] }[],
} => {
  const functionName = "calcTotalMaterialCostsByPartNoWithMonthlyData";
  try {
    // 月別材料費を計算
    const totalMaterialCostsByPartNoMonth = calcTotalMaterialCostsByPartNoMonthWithMonthlyData(
      quantitiesByPartNoMonth,
      materialCostByPartNoByMonth,
      yearMonthList
    );

    // 年度別材料費を集計
    const totalMaterialCostsByPartNoYear = totalMaterialCostsByPartNoMonth.reduce<
      { partNo: string, totalMaterialCosts: Decimal[] }[]
    >((acc, materialData) => {
      const partNo = materialData.partNo;
      const year = materialData.year;

      // 該当する PartNo の年度データを取得または新規作成
      let partNoData = acc.find(item => item.partNo === partNo);
      if (!partNoData) {
        partNoData = { partNo, totalMaterialCosts: [] };
        acc.push(partNoData);
      }

      // 年度インデックスを計算
      const yearIndex = year - 1;

      // 年度別の材料費を合算
      partNoData.totalMaterialCosts[yearIndex] =
        (partNoData.totalMaterialCosts[yearIndex] || new Decimal(0))
          .plus(materialData.totalMaterialCosts.reduce((sum, value) => sum.plus(value), new Decimal(0)));

      return acc;
    }, []);

    return {
      totalMaterialCostsByPartNoMonth,
      totalMaterialCostsByPartNoYear,
    };
  } catch (error) {
    console.error(`${functionName} ERROR: `, error);
    return {
      totalMaterialCostsByPartNoMonth: [],
      totalMaterialCostsByPartNoYear: [],
    };
  }
};

/**
 * PartNo別材料費の算出(月別)
 *
 * 各月に対応した材料単価を使用して月別材料費を計算します。
 *
 * @param {{ partNo: string, year: number, quantities: Decimal[] }[]} quantitiesByPartNoMonth PartNo別数量(月別)
 * @param {{ partNo: string, materialCostByMonth: Record<string, Decimal> }[]} materialCostByPartNoByMonth PartNo別月別材料単価
 * @param {{ year: number; month: number }[]} yearMonthList 年月リスト（年度ベース）
 * @returns {{ partNo: string, year: number, totalMaterialCosts: Decimal[] }[]} PartNo別材料費(月別)
 */
export const calcTotalMaterialCostsByPartNoMonthWithMonthlyData = (
  quantitiesByPartNoMonth: { partNo: string, year: number, quantities: Decimal[] }[],
  materialCostByPartNoByMonth: { partNo: string, materialCostByMonth: Record<string, Decimal> }[],
  yearMonthList: { year: number; month: number }[]
): { partNo: string, year: number, totalMaterialCosts: Decimal[] }[] => {
  const functionName = "calcTotalMaterialCostsByPartNoMonthWithMonthlyData";
  try {
    // 年度ごとにグループ化された月リストを作成
    const yearToMonthIndices: Map<number, { year: number; month: number; globalIndex: number }[]> = new Map();
    yearMonthList.forEach((ym, globalIndex) => {
      // 年度を算出（4月始まり）
      const fiscalYear = ym.month >= 4 ? ym.year : ym.year - 1;
      if (!yearToMonthIndices.has(fiscalYear)) {
        yearToMonthIndices.set(fiscalYear, []);
      }
      yearToMonthIndices.get(fiscalYear)!.push({ ...ym, globalIndex });
    });

    return quantitiesByPartNoMonth.map(quantityData => {
      const partNo = quantityData.partNo;
      const year = quantityData.year;  // 1, 2, 3... の年度番号

      // 該当するPartNoの月別材料単価を取得
      const materialData = materialCostByPartNoByMonth.find(item => item.partNo === partNo);
      const materialCostByMonth = materialData ? materialData.materialCostByMonth : {};

      // 月次数量を取得
      const monthlyQuantities = quantityData.quantities;

      // 年度の開始インデックスを計算
      let startIndex = 0;
      for (let y = 1; y < year; y++) {
        // 年度1は初年度の月数分、それ以降は12ヶ月分
        const prevYearQuantities = quantitiesByPartNoMonth.find(
          q => q.partNo === partNo && q.year === y
        );
        startIndex += prevYearQuantities ? prevYearQuantities.quantities.length : 0;
      }

      // 月次材料費を計算
      const monthlyMaterialCosts = monthlyQuantities.map((quantity, monthIndex) => {
        // 対応する年月を取得
        const globalIndex = startIndex + monthIndex;
        if (globalIndex >= yearMonthList.length) {
          return new Decimal(0);
        }
        const { year: calYear, month } = yearMonthList[globalIndex];
        const yearMonthKey = `${calYear}-${String(month).padStart(2, '0')}`;

        // 該当月の材料単価を取得（なければ0）
        const materialCost = materialCostByMonth[yearMonthKey] ?? new Decimal(0);

        // 材料費 = 数量 * 材料単価
        return quantity.times(materialCost);
      });

      return {
        partNo,
        year,
        totalMaterialCosts: monthlyMaterialCosts,
      };
    });
  } catch (error) {
    console.error(`${functionName} ERROR: `, error);
    return [];
  }
};

/**
 * PartNo別材料単価(加重平均)の取得(年度別)
 *
 * 月別の材料単価と数量から、年度別の加重平均材料単価を計算します。
 * 計算式: 加重平均材料単価 = Σ(材料単価 × 数量) / Σ(数量)
 *
 * 注意: quantitiesByPartNoMonth の quantities 配列は Object.entries による月の昇順（1, 2, 3, ...）で
 *       格納されているため、yearMonthList も月の昇順にソートしてからマッチングする必要があります。
 *
 * @param {{ partNo: string, materialCostByMonth: Record<string, Decimal> }[]} materialCostByPartNoByMonth PartNo別月別材料単価
 * @param {{ partNo: string, year: number, quantities: Decimal[] }[]} quantitiesByPartNoMonth PartNo別数量(月別)
 * @param {{ year: number; month: number }[]} yearMonthList 年月リスト（年度ベース）
 * @returns {{ partNo: string, materialCosts: Decimal[] }[]} 年度別 PartNo 材料単価データ（加重平均）
 */
export const getMaterialCostByPartNoYear = (
  materialCostByPartNoByMonth: { partNo: string, materialCostByMonth: Record<string, Decimal> }[],
  quantitiesByPartNoMonth: { partNo: string, year: number, quantities: Decimal[] }[],
  yearMonthList: { year: number; month: number }[]
): { partNo: string, materialCosts: Decimal[] }[] => {
  const functionName = "getMaterialCostByPartNoYear";
  try {
    // PartNoのユニークリストを取得
    const partNos = [...new Set(quantitiesByPartNoMonth.map(q => q.partNo))];

    return partNos.map(partNo => {
      // 該当PartNoの月別材料単価を取得
      const materialData = materialCostByPartNoByMonth.find(m => m.partNo === partNo);
      const materialCostByMonth = materialData ? materialData.materialCostByMonth : {};

      // 該当PartNoの年度別数量データを取得
      const partNoQuantities = quantitiesByPartNoMonth.filter(q => q.partNo === partNo);

      // 年度ごとの加重平均を計算
      const yearlyMaterialCosts: Decimal[] = [];

      // 年度の開始インデックスを追跡
      let yearMonthIndex = 0;

      partNoQuantities.forEach(({ year, quantities }) => {
        let totalCost = new Decimal(0);      // Σ(材料単価 × 数量)
        let totalQuantity = new Decimal(0);  // Σ(数量)

        // この年度に対応する yearMonthList の部分を取得
        const yearMonthsSlice = yearMonthList.slice(yearMonthIndex, yearMonthIndex + quantities.length);

        // quantities 配列は Object.entries による月の昇順（1, 2, 3, ...）で格納されているため、
        // yearMonthList も月の昇順にソートしてマッチングする
        const sortedByMonth = [...yearMonthsSlice].sort((a, b) => a.month - b.month);

        quantities.forEach((quantity, monthIdx) => {
          // ソート後の年月を取得
          const ym = sortedByMonth[monthIdx];
          if (!ym) return;

          const yearMonthKey = `${ym.year}-${String(ym.month).padStart(2, '0')}`;

          // 該当月の材料単価を取得（なければ0）
          const materialCost = materialCostByMonth[yearMonthKey] ?? new Decimal(0);

          // 加重和を計算
          totalCost = totalCost.plus(materialCost.times(quantity));
          totalQuantity = totalQuantity.plus(quantity);
        });

        // 年度の加重平均を計算（数量が0の場合は0）
        const weightedAverage = totalQuantity.gt(0)
          ? totalCost.div(totalQuantity)
          : new Decimal(0);

        yearlyMaterialCosts.push(weightedAverage);

        // 次の年度のインデックスに進める
        yearMonthIndex += quantities.length;
      });

      return {
        partNo,
        materialCosts: yearlyMaterialCosts,
      };
    });
  } catch (error) {
    console.error(`${functionName} ERROR: `, error);
    return [];
  }
};

// #region 仕損費単価計算

/**
 * Levelの階層を解析する
 * @param level Level文字列（例: "-", "*01", "**02", "***03"）
 * @returns 階層の深さ（0が最上位）
 */
const parseLevelDepth = (level: string | undefined): number => {
  if (!level || level === "-") {
    return 0;
  }
  // 先頭の'*'の数をカウント
  let depth = 0;
  for (const char of level) {
    if (char === '*') {
      depth++;
    } else {
      break;
    }
  }
  return depth;
};

/**
 * 指定行の傘下の行インデックス範囲を取得する
 * @param depths 事前計算済みの階層深さ配列
 * @param currentIndex 現在の行インデックス
 * @returns 傘下の行インデックス範囲 [startIndex, endIndex]（endIndexは含む）
 */
const getSubordinateRangeWithDepths = (
  depths: number[],
  currentIndex: number
): [number, number] => {
  const currentDepth = depths[currentIndex];

  // startIndex: 自身の次の行から開始（自身は含まない）
  const startIndex = currentIndex + 1;

  // endIndex: 自身の次の行から、同じまたはより浅い階層が出現するまでを傘下とする
  let endIndex = currentIndex;

  for (let i = currentIndex + 1; i < depths.length; i++) {
    // 同じか浅い階層が出現したら終了
    if (depths[i] <= currentDepth) {
      break;
    }
    endIndex = i;
  }

  return [startIndex, endIndex];
};

/**
 * 全行の傘下範囲を事前計算する
 * @param rows 全行データ
 * @returns 各行の傘下範囲配列 [[startIndex, endIndex], ...]
 */
const precomputeSubordinateRanges = (
  rows: ProcessBomRowForCalculation[]
): [number, number][] => {
  // 全行の階層深さを事前計算
  const depths = rows.map(row => parseLevelDepth(row.level));

  // 各行の傘下範囲を計算
  return rows.map((_, i) => getSubordinateRangeWithDepths(depths, i));
};

/**
 * 指定年月の仕損費単価を計算する（単一年月用）
 * @param rows ProcessBOM行データ配列
 * @param yearMonth 対象年月（"YYYY-MM"形式）
 * @param laborCostRate 労務費単価（賃率）
 * @param overheadRate Overhead単価
 * @param precomputedRanges 事前計算済みの傘下範囲（オプション、パフォーマンス向上用）
 * @returns 各行の仕損費単価計算結果
 */
export const calculateDefectCostUnitPriceForMonth = (
  rows: ProcessBomRowForCalculation[],
  yearMonth: string,
  laborCostRate: Decimal | number | string,
  overheadRate: Decimal | number | string,
  precomputedRanges?: [number, number][]
): DefectCostResult[] => {
  const functionName = "calculateDefectCostUnitPriceForMonth";

  try {
    if (!rows || rows.length === 0 || !yearMonth) {
      return [];
    }

    // 労務費単価とOverhead単価をDecimal型に変換（ループ外で1回のみ）
    const laborCost = new Decimal(laborCostRate || 0);
    const overhead = new Decimal(overheadRate || 0);
    const laborOverheadSum = laborCost.plus(overhead);
    const decimalOne = new Decimal(1);
    const decimalSixty = new Decimal(60);
    const decimalHundred = new Decimal(100);

    // 傘下範囲を事前計算（渡されていない場合のみ）
    const subordinateRanges = precomputedRanges || precomputeSubordinateRanges(rows);

    const results: DefectCostResult[] = [];

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const [startIdx, endIdx] = subordinateRanges[i];

      // 累積材料費と累積C/Tを1つのループで計算
      let cumulativeAmount = new Decimal(0);
      let cumulativeCycleTime = new Decimal(0);

      for (let j = startIdx; j <= endIdx; j++) {
        const currentRow = rows[j];

        // 累積材料費（累積Amount）
        const amount = currentRow.amountMonthly?.[yearMonth];
        if (amount && amount !== "") {
          try {
            cumulativeAmount = cumulativeAmount.plus(new Decimal(amount));
          } catch (e) {
            // 数値変換エラーは無視
          }
        }

        // 累積C/T（累積CycleTime）
        const cycleTime = currentRow.cycleTimeMonthly?.[yearMonth];
        if (cycleTime && cycleTime !== "") {
          try {
            cumulativeCycleTime = cumulativeCycleTime.plus(new Decimal(cycleTime));
          } catch (e) {
            // 数値変換エラーは無視
          }
        }
      }

      // 歩留（100%は100で格納されているので100で割る）
      const cumulativeYield = new Decimal(row.yieldMonthly?.[yearMonth] || "0").div(decimalHundred);

      // 仕損費単価を計算
      // 仕損費単価 = ((累積C/T × (労務費単価 + Overhead単価)) + 累積材料費) × (1 - 累積歩留)
      // ※累積C/Tは秒単位、労務費単価・経費単価は分単価なので、C/Tを60で割って分単位に変換
      const cumulativeCycleTimeInMinutes = cumulativeCycleTime.div(decimalSixty);
      const ctCost = cumulativeCycleTimeInMinutes.mul(laborOverheadSum);
      const totalCost = ctCost.plus(cumulativeAmount);
      const yieldFactor = decimalOne.minus(cumulativeYield);
      const defectCostUnitPrice = totalCost.mul(yieldFactor);

      results.push({
        seq: row.seq || "",
        level: row.level || "",
        cumulativeAmount,
        cumulativeCycleTime: cumulativeCycleTimeInMinutes,
        cumulativeYield: cumulativeYield.mul(decimalHundred), // 表示用に%に戻す
        defectCostUnitPrice,
      });
    }

    return results;

  } catch (error) {
    console.error(`${functionName} ERROR:`, error);
    return [];
  }
};

/**
 * 仕損費単価を年月別に計算する
 * @param rows ProcessBOM行データ配列
 * @param yearMonthList 年月リスト（"YYYY-MM"形式の配列）
 * @param laborCostRate 労務費単価（賃率）
 * @param overheadRate Overhead単価
 * @returns 各行の年月別仕損費単価計算結果
 */
export const calculateDefectCostUnitPrice = (
  rows: ProcessBomRowForCalculation[],
  yearMonthList: string[],
  laborCostRate: Decimal | number | string,
  overheadRate: Decimal | number | string
): DefectCostResultByMonth[] => {
  const functionName = "calculateDefectCostUnitPrice";

  try {
    if (!rows || rows.length === 0 || !yearMonthList || yearMonthList.length === 0) {
      return [];
    }

    // 傘下範囲を1回だけ事前計算（全年月で再利用）
    const precomputedRanges = precomputeSubordinateRanges(rows);

    // 各年月の計算結果をキャッシュ（重複計算を排除）
    const monthResultsCache: Map<string, DefectCostResult[]> = new Map();
    for (const yearMonth of yearMonthList) {
      const monthResults = calculateDefectCostUnitPriceForMonth(rows, yearMonth, laborCostRate, overheadRate, precomputedRanges);
      monthResultsCache.set(yearMonth, monthResults);
    }

    const results: DefectCostResultByMonth[] = [];

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const monthlyData: Record<string, DefectCostResult> = {};

      if (!row.cycleTimeMonthly || !Object.values(row.cycleTimeMonthly).some(v => v !== "")) {
        // プロセス行のみが対象
        continue;
      }

      // キャッシュから結果を取得
      for (const yearMonth of yearMonthList) {
        const cachedResults = monthResultsCache.get(yearMonth);
        if (cachedResults && cachedResults[i]) {
          monthlyData[yearMonth] = cachedResults[i];
        }
      }

      results.push({
        seq: row.seq || "",
        level: row.level || "",
        monthlyData,
      });
    }

    return results;

  } catch (error) {
    console.error(`${functionName} ERROR:`, error);
    return [];
  }
};

/**
 * PartNo別の仕損費を計算する
 *
 * 計算式: Σ(全行の仕損費単価) × 販売数量（PartNo別・年月別）
 *
 * @param bomDataByPartNo PartNo別のBOMデータ（キー: PartNo）
 * @param quantitiesByPartNoMonth PartNo別の月別販売数量
 * @param yearMonthList 年月リスト（"YYYY-MM"形式の配列）
 * @param laborCostRate 労務費単価（賃率）
 * @param overheadRate Overhead単価
 * @returns 月別仕損費（Decimal配列）
 */
export const calculateScrapCostByMonth = (
  bomDataByPartNo: Record<string, ProcessBomRowForCalculation[]>,
  quantitiesByPartNoMonth: { partNo: string, year: number, quantities: Decimal[] }[],
  yearMonthList: { year: number; month: number }[],
  laborCostRate: Decimal | number | string,
  overheadRate: Decimal | number | string,
  laborCostRateByMonth?: (yearMonth: string) => Decimal,
  overheadRateByMonth?: (yearMonth: string) => Decimal
): Decimal[] => {
  const functionName = "calculateScrapCostByMonth";

  try {
    console.log(`${functionName} START`);

    const totalMonths = yearMonthList.length;
    if (totalMonths === 0) {
      return [];
    }

    // 年月リストを"YYYY-MM"形式に変換（1回のみ）
    const yearMonthStrList = yearMonthList.map(({ year, month }) =>
      `${year}-${String(month).padStart(2, '0')}`
    );

    // 月数分の0で初期化
    const decimalZero = new Decimal(0);
    const scrapCostByMonth: Decimal[] = Array.from({ length: totalMonths }, () => new Decimal(0));

    // PartNo別に数量データをMapに変換（高速検索用）
    const quantitiesByPartNoMap: Map<string, { partNo: string, year: number, quantities: Decimal[] }[]> = new Map();
    for (const q of quantitiesByPartNoMonth) {
      const existing = quantitiesByPartNoMap.get(q.partNo);
      if (existing) {
        existing.push(q);
      } else {
        quantitiesByPartNoMap.set(q.partNo, [q]);
      }
    }

    // PartNo別に処理
    const partNos = Object.keys(bomDataByPartNo);
    for (const partNo of partNos) {
      const bomRows = bomDataByPartNo[partNo];

      if (!bomRows || bomRows.length === 0) {
        continue;
      }

      // 仕損費単価を計算
      // 月別単価関数が渡された場合は月ごとに異なる労務費/経費単価を使用
      const useMonthlyRates = !!(laborCostRateByMonth || overheadRateByMonth);
      const precomputedRanges = precomputeSubordinateRanges(bomRows);

      // 年月別の仕損費単価合計を事前計算
      const totalDefectCostByMonth: Decimal[] = new Array(totalMonths);
      if (useMonthlyRates) {
        // 振り返りモード: 月ごとに異なる単価で計算
        for (let monthIndex = 0; monthIndex < totalMonths; monthIndex++) {
          const yearMonthKey = yearMonthStrList[monthIndex];
          const monthLabor = laborCostRateByMonth ? laborCostRateByMonth(yearMonthKey) : new Decimal(laborCostRate);
          const monthOverhead = overheadRateByMonth ? overheadRateByMonth(yearMonthKey) : new Decimal(overheadRate);
          const monthResults = calculateDefectCostUnitPriceForMonth(bomRows, yearMonthKey, monthLabor, monthOverhead, precomputedRanges);
          let total = decimalZero;
          for (const result of monthResults) {
            if (result.defectCostUnitPrice) {
              total = total.plus(result.defectCostUnitPrice);
            }
          }
          totalDefectCostByMonth[monthIndex] = total;
        }
      } else {
        // 通常モード: 固定単価で一括計算（従来通り）
        const defectCostResults = calculateDefectCostUnitPrice(
          bomRows,
          yearMonthStrList,
          laborCostRate,
          overheadRate
        );
        for (let monthIndex = 0; monthIndex < totalMonths; monthIndex++) {
          const yearMonthKey = yearMonthStrList[monthIndex];
          let total = decimalZero;
          for (const result of defectCostResults) {
            const monthData = result.monthlyData[yearMonthKey];
            if (monthData) {
              total = total.plus(monthData.defectCostUnitPrice);
            }
          }
          totalDefectCostByMonth[monthIndex] = total;
        }
      }

      // 該当PartNoの数量データを取得し、年順にソート
      const partNoQuantities = quantitiesByPartNoMap.get(partNo);
      if (!partNoQuantities || partNoQuantities.length === 0) {
        continue;
      }
      partNoQuantities.sort((a, b) => a.year - b.year);

      // 累積月数マップを作成（各年の開始インデックス）
      const cumulativeMonthMap: Map<number, number> = new Map();
      let cumulativeMonths = 0;
      for (const pq of partNoQuantities) {
        cumulativeMonthMap.set(pq.year, cumulativeMonths);
        cumulativeMonths += pq.quantities.length;
      }

      // 年月ごとに処理
      for (let monthIndex = 0; monthIndex < totalMonths; monthIndex++) {
        const totalDefectCostUnitPrice = totalDefectCostByMonth[monthIndex];

        // 該当年月の販売数量を取得
        let quantity = decimalZero;
        for (const pq of partNoQuantities) {
          const startMonthIndex = cumulativeMonthMap.get(pq.year) ?? 0;
          const monthOffset = monthIndex - startMonthIndex;

          if (monthOffset >= 0 && monthOffset < pq.quantities.length) {
            const qty = pq.quantities[monthOffset];
            if (qty) {
              quantity = quantity.plus(qty);
            }
          }
        }

        // 仕損費 = 仕損費単価合計 × 販売数量
        const scrapCost = totalDefectCostUnitPrice.times(quantity);
        scrapCostByMonth[monthIndex] = scrapCostByMonth[monthIndex].plus(scrapCost);
      }
    }

    // console.log(`${functionName} RESULT: ${JSON.stringify(scrapCostByMonth.map(v => v.toNumber()))}`);
    return scrapCostByMonth;

  } catch (error) {
    console.error(`${functionName} ERROR:`, error);
    return [];
  }
};

/**
 * 客先指定部品の材料費（月別）を計算する
 *
 * BOMデータの isCustomerSpecifiedParts と materialCostWithInhouseProfit を
 * seq で紐づけて、客先指定部品の材料費を計算します。
 * materialCostWithInhouseProfitは既にローカル通貨で保存されているため、
 * この関数はローカル通貨ベースの金額を返します。
 *
 * @param customerSpecifiedSeqsByPartNo PartNo別の客先指定部品seqリスト
 * @param materialCostByPartNo PartNo別の materialCostWithInhouseProfit データ
 * @param quantitiesByPartNoMonth PartNo別月別販売数量
 * @param yearMonthList 年月リスト（"年度-月"形式ではなく実際の年月）
 * @returns 月別客先指定部品材料費（Decimal配列、ローカル通貨ベース）
 */
export const calculateCustomerSpecifiedPartsCostByMonth = (
  customerSpecifiedSeqsByPartNo: Record<string, string[]>,
  materialCostByPartNo: { partNo: string, materialCostData: CBomDataWithMonthly[] }[],
  quantitiesByPartNoMonth: { partNo: string, year: number, quantities: Decimal[] }[],
  yearMonthList: { year: number; month: number }[]
): Decimal[] => {
  const functionName = "calculateCustomerSpecifiedPartsCostByMonth";

  try {
    console.log(`${functionName} START`);

    // 月数分の0で初期化
    const totalMonths = yearMonthList.length;
    const customerSpecifiedPartsCostByMonth: Decimal[] = Array.from({ length: totalMonths }, () => new Decimal(0));

    // PartNo別に処理
    for (const { partNo, materialCostData } of materialCostByPartNo) {
      // 該当PartNoの客先指定部品seqリストを取得
      const customerSpecifiedSeqs = customerSpecifiedSeqsByPartNo[partNo];
      if (!customerSpecifiedSeqs || customerSpecifiedSeqs.length === 0) {
        continue;
      }

      // 客先指定部品のseqセットを作成
      const seqSet = new Set(customerSpecifiedSeqs);

      // 客先指定部品のseqに該当するmaterialCostDataをフィルタリング
      const customerSpecifiedMaterialCost = materialCostData.filter(item =>
        seqSet.has(String(item.seq))
      );

      if (customerSpecifiedMaterialCost.length === 0) {
        continue;
      }

      // 該当PartNoの数量データを取得し、年順にソート
      const partNoQuantities = quantitiesByPartNoMonth
        .filter(q => q.partNo === partNo)
        .sort((a, b) => a.year - b.year);

      // 累積月数マップを作成（各年の開始インデックス）
      // 1年目が端数月の場合も正しく計算するため
      const cumulativeMonthMap: Map<number, number> = new Map();
      let cumulativeMonths = 0;
      for (const pq of partNoQuantities) {
        cumulativeMonthMap.set(pq.year, cumulativeMonths);
        cumulativeMonths += pq.quantities.length;
      }

      // 年月ごとに処理
      yearMonthList.forEach(({ year, month }, monthIndex) => {
        const yearMonthKey = `${year}-${String(month).padStart(2, '0')}`;

        // 客先指定部品のInternalAdjustedAmountを合算（1個あたりの材料費）
        let totalAmountPerUnit = new Decimal(0);
        for (const item of customerSpecifiedMaterialCost) {
          const monthData = item.monthlyData?.[yearMonthKey];
          if (monthData?.InternalAdjustedAmount !== undefined && monthData?.InternalAdjustedAmount !== null) {
            try {
              const amount = new Decimal(monthData.InternalAdjustedAmount);
              totalAmountPerUnit = totalAmountPerUnit.plus(amount);
            } catch (e) {
              // 数値変換エラーは無視
            }
          }
        }

        // 該当年月の販売数量を取得
        let quantity = new Decimal(0);
        for (const pq of partNoQuantities) {
          // 累積月数から開始インデックスを取得（端数月対応）
          const startMonthIndex = cumulativeMonthMap.get(pq.year) ?? 0;
          const monthOffset = monthIndex - startMonthIndex;

          if (monthOffset >= 0 && monthOffset < pq.quantities.length) {
            quantity = quantity.plus(pq.quantities[monthOffset] || new Decimal(0));
          }
        }

        // 客先指定部品材料費 = 1個あたりの材料費合計 × 販売数量
        const customerSpecifiedCost = totalAmountPerUnit.times(quantity);
        customerSpecifiedPartsCostByMonth[monthIndex] = customerSpecifiedPartsCostByMonth[monthIndex].plus(customerSpecifiedCost);
      });
    }

    // console.log(`${functionName} RESULT: ${JSON.stringify(customerSpecifiedPartsCostByMonth.map(v => v.toNumber()))}`);
    return customerSpecifiedPartsCostByMonth;

  } catch (error) {
    console.error(`${functionName} ERROR:`, error);
    return [];
  }
};

// #endregion

// #region 労務費ベースアップ

/**
 * 労務費にベースアップ率を適用する共通関数（年度毎に複利で適用）
 * @param laborCostByMonth 月別労務費配列
 * @param baseLaborCostIncreaseRate ベースアップ率（%）
 * @param startDate プロジェクト開始日（YYYY-MM-DD形式）
 * @returns 調整後の月別労務費配列
 */
export const applyBaseUpRateToLaborCost = (
  laborCostByMonth: Decimal[],
  baseLaborCostIncreaseRate: number | null | undefined,
  startDate: string,
  originalProjectStartDate?: string,
): Decimal[] => {
  // ベースアップ率が0または未設定の場合はそのまま返す
  if (!baseLaborCostIncreaseRate) {
    return laborCostByMonth;
  }

  // ベースアップ値：(労務費のベースアップ率(%) / 100) + 1 (例：5% ⇒ 0.05 ⇒ 1.05)
  const baseIncreaseRate = new Decimal(baseLaborCostIncreaseRate).div(100).plus(1);

  // プロジェクト開始日からベースアップ率を計算
  const startDateObj = dayjs(startDate);
  // ベースアップの基準年度は元のPJ開始年度から算出（オーバーライドされている場合）
  const baseStartDateObj = originalProjectStartDate ? dayjs(originalProjectStartDate) : startDateObj;
  const baseStartYear = baseStartDateObj.year();
  const baseStartMonth = baseStartDateObj.month() + 1;
  const projectFiscalYearStart = baseStartMonth >= 4 ? baseStartYear : baseStartYear - 1;

  // 年度毎のベースアップ率(初年度:100% ⇒ 1)
  let yearlyBasePayRaiseRate = new Decimal(1);
  // 前回の年度インデックス（ベースアップ適用判定用）
  let previousFiscalYearIndex = 0;

  laborCostByMonth.forEach((_, index) => {
    // 現在の月を計算
    const currentDate = startDateObj.add(index, 'month');
    const currentYear = currentDate.year();
    const currentMonth = currentDate.month() + 1;
    // 年度を取得
    const fiscalYear = currentMonth >= 4 ? currentYear : currentYear - 1;
    // 年度インデックスを取得（1年目=0, 2年目=1, ...）
    const fiscalYearIndex = fiscalYear - projectFiscalYearStart;

    // 年度が変わった時のみベースアップ率を適用（2年目以降）
    if (fiscalYearIndex > previousFiscalYearIndex) {
      yearlyBasePayRaiseRate = yearlyBasePayRaiseRate.times(baseIncreaseRate);
      previousFiscalYearIndex = fiscalYearIndex;
    }

    // 労務費にベースアップ率を適用
    laborCostByMonth[index] = laborCostByMonth[index].times(yearlyBasePayRaiseRate);
  });

  return laborCostByMonth;
};

// #endregion
